import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Row, Col, Input, InputNumber, Button, Space, Spin, Empty, Modal, Tag, Typography,
  Card, Drawer, message, Descriptions, Table, Select, Switch, Timeline, Badge,
  Alert, Statistic, Form, Checkbox, Radio, Divider, Tooltip, Tabs, Collapse,
} from 'antd';
import {
  PlusOutlined, ThunderboltOutlined, DeleteOutlined,
  SearchOutlined, CheckCircleFilled, CloseCircleFilled,
  ClockCircleFilled, SyncOutlined, EditOutlined,
  CalendarOutlined,
  StopOutlined, ReloadOutlined, ApiOutlined,
  DownloadOutlined, UploadOutlined, HistoryOutlined,
  DatabaseOutlined, ExportOutlined, BookOutlined, InfoCircleOutlined,
  TableOutlined, FileTextOutlined, ExperimentOutlined, DisconnectOutlined,
  LockOutlined, WarningOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router';
import {
  listConnections, listConnectorSpecs, triggerSync,
  listCatalogAvailability,
  deleteConnection, updateConnection,
  cancelSyncStream, testConnection,
  createConnection, getConnectorSpec, getConnection,
  fetchConnectionsImportTemplate, importConnections,
  // Dagster-backed adapters: emit legacy-shaped run rows so the right
  // pane (built around the old /syncs API) keeps working unchanged.
  listSyncsViaDagster as listSyncs,
  getSyncRunViaDagster as getSyncRun,
  listDagitRuns, getDagitRun,
  getSchedulerStatus,
  PHASE, phaseLabel,
} from '../services/dataPlatformService';
import SchedulerControl, { connectorSensorKey } from '../components/SchedulerControl';
import JsonSchemaForm from '../components/JsonSchemaForm';
import SyncHistoryDialog from '../components/SyncHistoryDialog';
import ScheduleTimelineDialog from '../components/ScheduleTimelineDialog';
import { isUatMode } from '../utils/featureFlags';
import {
  sourceLagSummary, sourceLagExplanation,
} from '../utils/sourceFreshness';
import { ProBadge, EditionComparisonModal } from './discovery/proUpgrade';
// Dagit links go through tenant-api's /admin/dagit reverse proxy
// (same-origin). See services/ui/tenant-console/src/services/dagsterLink.js.
import { dagsterRunUrl, openDagster, openDagsterRun, isDagitEnabled } from '../services/dagsterLink';
import { getVendorQuotaStatus, checkVendorQuotaStatus } from '../services/orgStructureService';

const INHERIT = '__inherit__';
const CUSTOM = '__custom__';
const INCREMENTAL = 'incremental';
const PROCESSING = 'processing';
const UNAVAILABLE = 'UNAVAILABLE';
const NOT_CHECKED = 'not_checked';
const PHASE_LABEL_COMPACT_MAX_LEN = 28;
const PHASE_LABEL_COMPACT_WORDS = 3;
const COUNTDOWN_TICK_MS = 250;
const REFRESH_WINDOW_DEFAULT_MS = 5000;
const MANUAL_REFRESH_WINDOW_MS = 5000;
const AVAILABILITY_POLL_MS = 5000;
const UAT_SAMPLE_LIMIT_DEFAULT = 5;
const SENDING_CLOCK_SKEW_MS = 2000;
const SENDING_MAX_DISPLAY_MS = 30000;
const RUN_DETAIL_POLL_MS = 1500;
const CARD_OPACITY_PRO_LOCKED = 0.8;
const CARD_OPACITY_DISCONNECTED = 0.9;
const SAMPLE_PREVIEW_MAX_CHARS = 80;
const STREAM_HISTORY_MAX_RUNS = 20;

const { Title, Text, Paragraph } = Typography;

/**
 * DataSourcesPage — 50/50 two-column "connection hub".
 *
 * Per-source single-connection rule: a workspace can have at most one
 * connection per connector_name. Enforced in DB (unique constraint),
 * service layer (BadRequest on duplicate), and here in the UI.
 *
 * URL state:
 *   ?source=<connector_name>     selected connection tile
 *   ?stream=<stream_name>        opens stream-detail drawer (sync logs)
 *   ?edit=1                      expands the connection's edit form
 */

const CUSTOM_CRON_LABEL = 'Custom cron…';
const THEME_CONTENT_BG = 'var(--theme-contentBg, #fff)';
const CARD_BORDER = '1px solid var(--theme-border, #f0f0f0)';
const SCROLL_MAX_CONTENT = 'max-content';

const SCHEDULE_PRESETS = [
  { value: INHERIT, label: 'Inherit (use connection)' },
  { value: '0 * * * *', label: 'Hourly · :00' },
  { value: '0 */4 * * *', label: 'Every 4 hours' },
  { value: '0 2 * * *', label: 'Daily · 02:00 UTC' },
  { value: '0 2 * * 1', label: 'Weekly · Mon 02:00' },
  { value: '__manual__', label: 'Manual only' },
  { value: CUSTOM, label: CUSTOM_CRON_LABEL },
];

const CONNECTION_SCHEDULE_PRESETS = [
  { value: '',          label: 'Manual',         hint: 'Run only when triggered.' },
  { value: '0 * * * *', label: 'Hourly',         hint: 'Top of every hour, UTC.' },
  { value: '0 2 * * *', label: 'Daily 02:00',    hint: 'Once per day, UTC.' },
  { value: '0 2 * * 1', label: 'Weekly (Mon)',   hint: 'Mon 02:00 UTC.' },
  { value: '__custom',  label: CUSTOM_CRON_LABEL,   hint: 'Standard 5-field cron.' },
];

// Initial-backfill window choices for transactional streams (commits, PRs,
// incidents). Only meaningful for streams with a cursor_field. Values are
// the day count; 0 / __all__ means "no cap, extract everything".
const HISTORY_PRESETS = [
  { value: '__all__', label: 'All time',    days: 0 },
  { value: '7',       label: 'Last 7 days', days: 7 },
  { value: '30',      label: 'Last 30 days', days: 30 },
  { value: '90',      label: 'Last 90 days', days: 90 },
  { value: '180',     label: 'Last 6 months', days: 180 },
  { value: '365',     label: 'Last 1 year',  days: 365 },
  { value: CUSTOM, label: 'Custom days…', days: null },
];

// Per-stream load-mode override. `__inherit__` falls back to the
// connection-level `load_mode`. `incremental` only makes sense for streams
// with a cursor_field; the asset still works for non-cursor streams but
// behaves like a full load (the connector ignores `since`/`until`).
const LOAD_MODE_OPTIONS = [
  { value: INHERIT,  label: 'Inherit' },
  { value: INCREMENTAL,  label: 'Delta' },
  { value: 'full',         label: 'Full' },
];

const CATEGORY_LABELS = {
  'source-control': 'Source control',
  'ci-cd': 'CI / CD',
  'project-management': 'Project management',
  'incident-management': 'Incident management',
  'observability': 'Observability',
  'itsm': 'IT service management',
  'productivity': 'Productivity',
  'ai-coding': 'AI / coding assistants',
  // The catalog files every AI connector (Copilot, Claude, Cursor, OpenAI
  // API, Bedrock, M365) under 'ai-tools'.
  'ai-tools': 'AI tools',
  'security': 'Security',
  'other': 'Other',
};

const CATEGORY_ORDER = [
  'source-control', 'ci-cd', 'project-management', 'incident-management',
  'observability', 'itsm', 'ai-tools', 'ai-coding', 'productivity', 'security', 'other',
];

function categoryLabel(id) {
  if (CATEGORY_LABELS[id]) {return CATEGORY_LABELS[id];}
  return (id || 'other')
    .split(/[-_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function timeAgo(iso) {
  if (!iso) {return '—';}
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) {return 'just now';}
  const s = Math.floor(ms / 1000);
  if (s < 60) {return `${s}s ago`;}
  const m = Math.floor(s / 60);
  if (m < 60) {return `${m}m ago`;}
  const h = Math.floor(m / 60);
  if (h < 24) {return `${h}h ago`;}
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// Per-phase metadata used by `PhaseTag`. Phase is the user-facing
// progress signal: "Waiting in queue → Syncing data from source →
// Transforming data → Done". Status (success/failed/etc.) is the
// terminal-state signal. Both render at the same time so the card can
// show "Running · Transforming data".
const _PHASE_META = {
  [PHASE.QUEUED]:        { color: 'default',    icon: <ClockCircleFilled />,    spin: false },
  [PHASE.EXTRACTING]:    { color: PROCESSING, icon: <DownloadOutlined />,     spin: false },
  [PHASE.EXTRACTED]:     { color: PROCESSING, icon: <SyncOutlined spin />,    spin: true },
  [PHASE.TRANSFORMING]:  { color: PROCESSING, icon: <SyncOutlined spin />,    spin: true },
  [PHASE.SUCCESS]:       { color: 'success',    icon: <CheckCircleFilled />,    spin: false },
  [PHASE.FAILED]:        { color: 'error',      icon: <CloseCircleFilled />,    spin: false },
  [PHASE.PARTIAL]:       { color: 'warning',    icon: <CheckCircleFilled />,    spin: false },
  [PHASE.CANCELLED]:     { color: 'default',    icon: <StopOutlined />,         spin: false },
  [PHASE.STALE]:         { color: 'warning',    icon: <ClockCircleFilled />,    spin: false },
  [PHASE.UNKNOWN]:       { color: 'warning',    icon: <ClockCircleFilled />,    spin: false },
};

const PhaseTag = React.memo(function PhaseTag({ phase, detail, compact }) {
  if (!phase) {return null;}
  const meta = _PHASE_META[phase] || _PHASE_META[PHASE.UNKNOWN];
  const label = phaseLabel(phase) || phase;
  const text = compact && label.length > PHASE_LABEL_COMPACT_MAX_LEN
    ? label.split(' ').slice(0, PHASE_LABEL_COMPACT_WORDS).join(' ') + '…'
    : label;
  const tip = detail ? `${label} (${detail})` : label;
  return (
    <Tooltip title={tip}>
      <Tag color={meta.color} icon={meta.icon} style={{ margin: 0 }}>
        {text}
      </Tag>
    </Tooltip>
  );
});

function StatusTag({ status, stale, tooltip }) {
  const map = {
    success:   { color: 'success', icon: <CheckCircleFilled />, label: 'Success' },
    succeeded: { color: 'success', icon: <CheckCircleFilled />, label: 'Success' },
    failed:    { color: 'error',   icon: <CloseCircleFilled />, label: 'Failed' },
    running:   { color: PROCESSING, icon: <SyncOutlined spin />, label: 'Running' },
    queued:    { color: 'default', icon: <ClockCircleFilled />, label: 'Queued' },
    cancelled: { color: 'default', icon: <StopOutlined />, label: 'Cancelled' },
    skipped:   { color: 'default', icon: <ClockCircleFilled />, label: 'Skipped' },
    partial_success: { color: 'warning', icon: <CheckCircleFilled />, label: 'Partial' },
    unknown:   { color: 'warning', icon: <ClockCircleFilled />, label: 'Unknown' },
  };
  const cfg = stale
    ? { color: 'warning', icon: <ClockCircleFilled />, label: 'Stale' }
    : (map[status] || { color: 'default', icon: null, label: status || 'Idle' });
  const tag = <Tag color={cfg.color} icon={cfg.icon} style={{ margin: 0 }}>{cfg.label}</Tag>;
  if (tooltip || stale) {
    return (
      <Tooltip title={tooltip || 'No update from the orchestrator for a while — the run may be orphaned.'}>
        {tag}
      </Tooltip>
    );
  }
  return tag;
}

// ─────────────────────────────────────────────────────────────
// RefreshCountdown — Dagster-style top-right indicator.
//
// The page always auto-refreshes (5s when a sync is in flight,
// 10s when idle); this pill ticks down to the next poll and
// doubles as a manual refresh button. Rendered in neutral gray
// to stay out of the user's way.
// ─────────────────────────────────────────────────────────────
function RefreshCountdown({ nextRefreshAt, onRefresh }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), COUNTDOWN_TICK_MS);
    return () => clearInterval(t);
  }, []);
  const target = nextRefreshAt || now;
  const remainingMs = Math.max(0, target - now);
  // Capture the full window each time the parent reschedules so the
  // conic gauge drains smoothly when the interval flips between the
  // active (5s) and idle (10s) refresh rates.
  const [windowMs, setWindowMs] = useState(REFRESH_WINDOW_DEFAULT_MS);
  useEffect(() => {
    if (!nextRefreshAt) {return;}
    const w = Math.max(1000, nextRefreshAt - Date.now());
    setWindowMs(w);
  }, [nextRefreshAt]);
  const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const pct = Math.max(0, Math.min(100, 100 - (remainingMs / windowMs) * 100));
  const label = `Refreshing in ${seconds}s`;
  const tip = 'Auto-refreshing. Click to refresh now.';
  return (
    <Tooltip title={tip} placement="bottomLeft">
      <div
        role="button"
        tabIndex={0}
        onClick={onRefresh}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') {onRefresh();} }}
        style={{
          position: 'absolute',
          top: 12,
          right: 20,
          zIndex: 50,
          cursor: 'pointer',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 12px',
          fontSize: 12,
          color: 'var(--theme-textSecondary, #595959)',
          background: THEME_CONTENT_BG,
          border: '1px solid var(--theme-border, #d9d9d9)',
          borderRadius: 16,
          boxShadow: '0 1px 3px var(--theme-shadow, rgba(0,0,0,0.06))',
        }}
      >
        <span
          aria-hidden
          style={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: `conic-gradient(var(--theme-textIcon, #8c8c8c) ${pct}%, var(--theme-border, #f0f0f0) ${pct}% 100%)`,
          }}
        />
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{label}</span>
      </div>
    </Tooltip>
  );
}

// ─────────────────────────────────────────────────────────────
// Pure helpers extracted from DataSourcesPage() and its effects.
// Kept top-level (no closures over component state) so each has its
// own small, testable surface — see DataSourcesPage.test.js.
// ─────────────────────────────────────────────────────────────

// refresh({runsOnly:true}): true when `runs` reports the same set of rows
// (id/status/finished_at/stale, same order) as `prev` — lets setAllRuns
// bail out and keep the previous array reference so memoised consumers
// don't see a churn-triggered reference change (the main flicker fix).
export function sameRunsSnapshot(prev, runs) {
  const next = runs || [];
  if (prev.length !== next.length) {return false;}
  for (let i = 0; i < prev.length; i++) {
    const a = prev[i];
    const b = next[i];
    if (
      a.id !== b.id ||
      a.status !== b.status ||
      a.finished_at !== b.finished_at ||
      a.stale !== b.stale
    ) {
      return false;
    }
  }
  return true;
}

// Pending-sync reconcile effect: the most recent started_at (epoch ms)
// among `allRuns` belonging to `connId`, or 0 when none exist yet.
export function newestRunStartedAtMs(allRuns, connId) {
  return (allRuns || [])
    .filter((r) => r.connection_id === connId)
    .reduce((m, r) => {
      const t = r.started_at ? new Date(r.started_at).getTime() : 0;
      return Math.max(m, t);
    }, 0);
}

// Fetch run-detail for many run ids in parallel, tolerating individual
// failures (null for any id whose detail fetch failed) so one bad run
// doesn't blank the whole backfill list.
export function fetchManyRunDetails(ids, fallbackStreamNames) {
  return Promise.all(
    ids.map((id) => getSyncRun(id, { fallbackStreamNames }).catch(() => null)),
  );
}

// sourceName -> the one Connection row for it, or null (no connection,
// or no source selected).
export function resolveSelectedConnection(sourceName, connectionBySource) {
  return sourceName ? connectionBySource[sourceName] || null : null;
}

// Derive the "latest run" view used both to drive the per-run detail
// poller and to compute display state, from the selected connection's
// already-sorted-desc run list.
export function latestRunProjection(selectedRuns, selectedConnection) {
  const latestSelectedRun = selectedRuns.length > 0 ? selectedRuns[0] : null;
  return {
    latestRunId: latestSelectedRun ? latestSelectedRun.id : null,
    latestRunStatus: latestSelectedRun ? latestSelectedRun.status : null,
    latestRunPhase: latestSelectedRun ? latestSelectedRun.phase : null,
    selectedConnId: selectedConnection ? selectedConnection.id : null,
    fallbackKey: selectedConnection ? (selectedConnection.selected_streams || []).join(',') : '',
  };
}

// Right pane mode: create-form vs. disconnected-preview vs. connected
// view. `hasSpec` is whether a catalog tile is selected at all.
export function resolveRightPaneMode({ hasSpec, isConnected, wantsConnectForm }) {
  return {
    showCreateForm: !!hasSpec && !isConnected && wantsConnectForm,
    showDisconnectedPreview: !!hasSpec && !isConnected && !wantsConnectForm,
  };
}

// Daemon health for the Scheduler controls, and whether the orchestrator is
// deliberately absent.
function schedulerHealthFlags(schedulerStatus) {
  const schedulerDaemonHealthy = !!(
    schedulerStatus && schedulerStatus.daemon && schedulerStatus.daemon.healthy
  );
  // The orchestrator is deliberately absent in cost-reduced environments
  // (data_ingestion_orchestrator_*_desired_count = 0). Distinguish that from
  // "the daemon is unhealthy": both leave the sync history empty, but only one
  // of them is something to fix. `orchestrator_available` is sent explicitly by
  // /syncs/dagit/scheduler-status; older responses omit it, and an absent flag
  // must not be read as "absent orchestrator".
  const orchestratorAbsent = !!(
    schedulerStatus && schedulerStatus.orchestrator_available === false
  );
  return { schedulerDaemonHealthy, orchestratorAbsent };
}

// "Test all" / "Sync all" (+ the UAT replay) across every connection.
function BulkActions({
  connections, uatMode, testingAll, handleTestAll, syncingAll, handleSyncAll,
  syncingAllUat, handleSyncAllUat, bulkUatSampleLimit, setBulkUatSampleLimit,
}) {
  return (
    <Space size={4} wrap>
      <Tooltip title="Run Test Connection on every existing connection. Refreshes total/sample data without ingesting.">
        <Button
          size="small"
          icon={<ReloadOutlined />}
          loading={testingAll}
          disabled={!connections.length}
          onClick={handleTestAll}
        >
          Test all
        </Button>
      </Tooltip>
      <Tooltip title="Trigger a sync for every existing connection. Each sync runs in Dagster; the activity panel updates automatically.">
        <Button
          size="small"
          type="primary"
          icon={<ThunderboltOutlined />}
          loading={syncingAll}
          disabled={!connections.length}
          onClick={handleSyncAll}
        >
          Sync all
        </Button>
      </Tooltip>
      {uatMode && (
        <Space.Compact>
          <Tooltip title="Number of rows to retrieve per stream. Each connection's Test Connection cache is refreshed to this depth before its replay run launches.">
            <Select
              size="small"
              value={bulkUatSampleLimit}
              onChange={setBulkUatSampleLimit}
              disabled={syncingAllUat || !connections.length}
              style={{ width: 88 }}
              options={[5, 10, 25, 50, 100, 250, 500, 1000].map((n) => ({
                value: n,
                label: `${n} rows`,
              }))}
            />
          </Tooltip>
          <Tooltip title="UAT only — for every connection, refresh cached Test Connection samples to the selected row count, then push them through the full sync pipeline (bronze → dbt → silver). No live source calls during the replay step.">
            <Button
              size="small"
              icon={<ThunderboltOutlined />}
              loading={syncingAllUat}
              disabled={!connections.length}
              onClick={handleSyncAllUat}
            >
              Sync all &mdash; UAT
            </Button>
          </Tooltip>
        </Space.Compact>
      )}
    </Space>
  );
}

// Dagster, sync history, schedule timeline, export / import.
function PipelineTools({
  orchestratorAbsent, setSyncHistoryOpen, setScheduleTimelineOpen, handleExportTemplate,
  importBusy, importInputRef, onPickImportFile,
}) {
  return (
    <div style={{ marginTop: 8 }}>
      <Space size={4} wrap>
        {/* Two conditions, both about whether Dagit can actually be
            opened: the proxy has to be mounted on this deployment AND
            the orchestrator has to be up. Either one missing and this
            button is a dead end — the proxy answers 503 when the
            webserver isn't running, which is the normal state on a
            cost-reduced environment. Unlike the Schedule timeline
            button below, there is no useful empty state to show here,
            so it hides rather than explains itself in a tooltip. */}
        {isDagitEnabled() && !orchestratorAbsent && (
          <Tooltip title="Open the Data Pipeline overview in Dagster (all code locations + recent runs).">
            <Button
              size="small"
              onClick={() => openDagster('/')}
            >
              Open Data Pipeline in Dagster
            </Button>
          </Tooltip>
        )}
        {/* Enabled even with no orchestrator. The dialog answers 200 with an
            empty list in that case, so it opens and shows its own empty
            state; disabling the button instead took away a surface that
            still works and told the reader nothing the banner above does
            not already say. The tooltip carries the reason. */}
        <Tooltip title={orchestratorAbsent
          ? 'Scheduled ingestion is off in this environment, so there is no past sync activity to list.'
          : 'View Dagster sync activity for this workspace'}>
          <Button
            size="small"
            icon={<HistoryOutlined />}
            onClick={() => setSyncHistoryOpen(true)}
          >
            Sync history
          </Button>
        </Tooltip>
        <Tooltip title="See every connection's schedule projected on a timeline (last 7 days → next 7 days by default), overlaid with actual runs.">
          <Button
            size="small"
            icon={<CalendarOutlined />}
            onClick={() => setScheduleTimelineOpen(true)}
          >
            Schedule timeline
          </Button>
        </Tooltip>
        <Tooltip title="Download a JSON snapshot of every connector slot, including the current config and credentials of existing connections. Edit and re-upload to bulk-update.">
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={handleExportTemplate}
          >
            Export
          </Button>
        </Tooltip>
        <Tooltip title="Upload a filled template JSON. Each entry with enabled=true will be created (or updated when overwrite=true) and tested.">
          <Button
            size="small"
            icon={<UploadOutlined />}
            loading={importBusy}
            onClick={() => importInputRef.current && importInputRef.current.click()}
          >
            Import
          </Button>
        </Tooltip>
        <input
          ref={importInputRef}
          type="file"
          accept="application/json,.json"
          style={{ display: 'none' }}
          onChange={onPickImportFile}
        />
      </Space>
    </div>
  );
}

// Right pane: nothing picked / disconnected preview / create form /
// connected view.
function RightPane({
  selectedSpec, selectedConnection, showDisconnectedPreview, showCreateForm,
  setUrl, refresh, editing, latestRunDetail, extraRunDetails, selectedRuns,
  schedulerByConnector, schedulerDaemonHealthy, setSchedulerStatus, syncingByConn, markPendingSync,
}) {
  if (!selectedSpec) {return <EmptyRightPane />;}
  if (showDisconnectedPreview) {
    return (
      <DisconnectedSourcePreview
        spec={selectedSpec}
        onConnect={() => setUrl({ connect: '1' })}
      />
    );
  }
  if (showCreateForm) {
    return (
      <ConnectionForm
        mode="create"
        spec={selectedSpec}
        onCancel={() => setUrl({ connect: '' })}
        onSaved={async () => {
          setUrl({ connect: '' });
          await refresh({ silent: true });
        }}
      />
    );
  }
  return (
    <ConnectedSourceView
      spec={selectedSpec}
      connection={selectedConnection}
      latestRunDetail={latestRunDetail}
      extraRunDetails={extraRunDetails}
      recentRuns={selectedRuns}
      schedulerSensor={selectedConnection ? schedulerByConnector[connectorSensorKey(selectedConnection.connector_name)] : null}
      schedulerDaemonHealthy={schedulerDaemonHealthy}
      onSchedulerChanged={setSchedulerStatus}
      editing={editing}
      syncing={!!(selectedConnection && syncingByConn[selectedConnection.id])}
      onStartEdit={() => setUrl({ edit: '1' })}
      onCancelEdit={() => setUrl({ edit: '' })}
      onOpenStream={(stream) => setUrl({ stream })}
      onChange={() => refresh({ silent: true })}
      onSyncStart={markPendingSync}
      onDeleted={async () => {
        await refresh({ silent: true });
        setUrl({ edit: '', stream: '' });
      }}
    />
  );
}

export default function DataSourcesPage() {
  const [params, setParams] = useSearchParams();
  const sourceName = params.get('source') || '';
  const streamName = params.get('stream') || '';
  const editing = params.get('edit') === '1';

  const [connections, setConnections] = useState([]);
  const [specs, setSpecs] = useState([]);
  // Per-connector availability (Dagster code-location load status).
  // Map: connector_name -> { state: 'AVAILABLE'|'UNAVAILABLE', message?, since? }.
  // Empty object means "treat everything as available" (the endpoint
  // returned nothing or the orchestrator is unreachable).
  const [availability, setAvailability] = useState({});
  const [allRuns, setAllRuns] = useState([]);
  // Dagster scheduler state (SENSOR daemon + per-connector schedule sensors),
  // powering the per-connection Scheduler Start/Stop control. null until loaded.
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [latestRunDetail, setLatestRunDetail] = useState(null);
  // Details of the recent non-latest runs of the selected connection.
  // Needed because per-stream syncs create runs scoped to a single
  // stream, so `latestRunDetail.streams` drops other streams' history
  // the moment a newer per-stream run appears. We backfill from the
  // recent run window so each stream's row keeps its true last status.
  const [extraRunDetails, setExtraRunDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Bulk import/export state. Users can download a JSON template covering
  // every connector, fill in their credentials offline, and re-upload it
  // to provision (or refresh) connections in one round-trip — convenient
  // for CI / onboarding flows where clicking through the wizard 10× is
  // tedious.
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importInputRef = React.useRef(null);

  // Bulk "Test All" / "Sync All" header actions — fan out one call per
  // existing connection. Both run sequentially so we never DDOS the
  // sources, and surface a per-source success/failure summary at the end.
  const [testingAll, setTestingAll] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingAllUat, setSyncingAllUat] = useState(false);
  const [bulkUatSampleLimit, setBulkUatSampleLimit] = useState(UAT_SAMPLE_LIMIT_DEFAULT);

  // Auto-refresh countdown — surfaced as a Dagster-style pill in the
  // top-right of the page so users can see when the next poll will
  // fire (and click to refresh on demand). `nextRefreshAt` is an
  // absolute epoch ms; null means polling is paused (no active runs).
  const [nextRefreshAt, setNextRefreshAt] = useState(null);

  // UAT-only affordances (Sync now — UAT). Hidden in production
  // builds; toggle via REACT_APP_UAT_MODE env or `localStorage.uatMode`.
  const uatMode = isUatMode();

  // Show all connection slots (including unconnected catalog entries) vs.
  // only the ones with an active connection. Most of the time operators
  // want a "fleet status" view of just their live connections.
  const [showOnlyConnected, setShowOnlyConnected] = useState(false);
  // Hide connectors whose code location is reported as UNAVAILABLE by the
  // orchestrator so the list only shows slots a tenant can actually use.
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);

  // Per-connection sync-button busy state — keyed by connection id so each
  // card's button has its own spinner. Independent of the global polling
  // refresh and of the selected-connection ConnectedSourceView state.
  // Value is the click timestamp (ms) — used by the reconcile effect
  // below to enforce a minimum-display window for the "Sending…" tag.
  const [syncingByConn, setSyncingByConn] = useState({});

  // Stamp / clear the pending-sync flag for a connection. Stamping uses
  // the current click time so the reconcile effect can match it against
  // the next Dagster run's started_at.
  const markPendingSync = useCallback((connId) => {
    setSyncingByConn((s) => ({ ...s, [connId]: Date.now() }));
  }, []);
  const clearPendingSync = useCallback((connId) => {
    setSyncingByConn((s) => {
      const next = { ...s };
      delete next[connId];
      return next;
    });
  }, []);

  // Sync history modal — Dagster-backed activity, opened from the
  // connections panel header (above the app list).
  const [syncHistoryOpen, setSyncHistoryOpen] = useState(false);
  // Schedule timeline modal — full-screen projection of every connection's
  // cron schedule across a window, overlaid with actual runs.
  const [scheduleTimelineOpen, setScheduleTimelineOpen] = useState(false);

  // `silent` skips the loading spinner. `runsOnly` skips connection /
  // catalog refetch — the live polling loop only needs run rows, and
  // refetching all three on every 5s tick causes the cards to remount
  // (visible flicker). The full refresh runs once on mount and after
  // explicit user actions (create / delete / import).
  const refresh = useCallback(async ({ silent = false, runsOnly = false } = {}) => {
    if (!silent) {setLoading(true);}
    try {
      if (runsOnly) {
        const latestRuns = await listSyncs().catch(() => []);
        // Cheap reference-equality optimisation: if the run list hasn't
        // moved, keep the previous array so memoised consumers don't see
        // a new reference and re-render. This is the main flicker fix.
        setAllRuns((prev) => (sameRunsSnapshot(prev, latestRuns) ? prev : (latestRuns || [])));
        return;
      }
      const [conns, catalog, runs, avail, sched] = await Promise.all([
        listConnections().catch(() => []),
        listConnectorSpecs().catch(() => []),
        listSyncs().catch(() => []),
        listCatalogAvailability().catch(() => ({})),
        getSchedulerStatus().catch(() => null),
      ]);
      setConnections(conns || []);
      setSpecs(catalog || []);
      setAllRuns(runs || []);
      setAvailability(avail || {});
      setSchedulerStatus(sched);
    } catch (err) {
      message.error(`Failed to load: ${err.message}`);
    } finally {
      if (!silent) {setLoading(false);}
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Per-connector schedule-sensor lookup (keyed by underscored connector form)
  // + daemon health, for the per-connection Scheduler control on cards and the
  // detail header. setScheduleSensor returns the refreshed status, so the
  // control's onChanged can update this state directly without a full reload.
  const schedulerByConnector = useMemo(() => {
    const m = {};
    for (const s of (schedulerStatus && schedulerStatus.sensors) || []) {m[s.connector] = s;}
    return m;
  }, [schedulerStatus]);
  const { schedulerDaemonHealthy, orchestratorAbsent } = schedulerHealthFlags(schedulerStatus);

  // Re-poll Dagster code-location load status while any connector is
  // still importing in Dagster. Two trigger conditions:
  //   1. /availability says some location is in state LOADING.
  //   2. A Connection row exists for a connector but /availability has no
  //      entry yet — the race between a fresh connect and Dagster picking
  //      up the new workspace.yaml.
  // Once every connected connector has settled to LOADED or ERROR the
  // poll stops, so the page is idle the rest of the time.
  useEffect(() => {
    const anyLoading = Object.values(availability || {})
      .some((v) => v && v.state === 'LOADING');
    const anyMissing = (connections || []).some(
      (c) => c?.connector_name && !availability[c.connector_name]
    );
    if (!anyLoading && !anyMissing) {return undefined;}
    const t = setInterval(() => {
      listCatalogAvailability()
        .then((avail) => setAvailability(avail || {}))
        .catch(() => {});
    }, AVAILABILITY_POLL_MS);
    return () => clearInterval(t);
  }, [availability, connections]);

  // ── Bulk export: download a JSON snapshot of every connector slot ──
  // Includes the *current* config for connections that already exist, plus
  // placeholder entries for connectors that don't. Credential fields come
  // back redacted (the UI never passes `reveal_secrets`) — re-importing a
  // redacted entry keeps the stored secret via merge_config. Users edit the
  // file and re-upload to update non-secret fields, or credentials, in bulk.
  const handleExportTemplate = useCallback(async () => {
    try {
      const tpl = await fetchConnectionsImportTemplate();
      const blob = new Blob([JSON.stringify(tpl, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `connections-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      message.success('Connections exported — credential fields are redacted; re-import keeps the stored secret.');
    } catch (err) {
      message.error(`Export failed: ${err.message}`);
    }
  }, []);

  // ── Bulk import: read a filled template and POST to /connections/import ──
  const handleImportFile = useCallback(async (file) => {
    if (!file) {return;}
    setImportBusy(true);
    try {
      const text = await file.text();
      let parsed;
      try { parsed = JSON.parse(text); }
      catch (e) { throw new Error(`Invalid JSON: ${e.message}`); }
      const connectors = Array.isArray(parsed) ? parsed : parsed.connectors;
      if (!Array.isArray(connectors)) {
        throw new Error('Expected an object with a `connectors` array, or a top-level array.');
      }
      const result = await importConnections({ connectors, run_test_connection: true });
      setImportResult(result);
      await refresh({ silent: true });
      const summary = `Created ${result.created} · Updated ${result.updated} · Skipped ${result.skipped} · Failed ${result.failed}`;
      if (result.failed > 0) {message.warning(`Import finished with errors. ${summary}`);}
      else {message.success(`Import complete. ${summary}`);}
    } catch (err) {
      message.error(`Import failed: ${err.message}`);
    } finally {
      setImportBusy(false);
    }
  }, [refresh]);

  const onPickImportFile = useCallback((e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';  // allow re-selecting the same file
    if (file) {handleImportFile(file);}
  }, [handleImportFile]);

  // ── Bulk: Test all existing connections in sequence ──────────────────
  // Sequential (not parallel) so we don't hammer external APIs and so the
  // user sees deterministic progress messages. Aggregates the results
  // into one toast at the end.
  const handleTestAll = useCallback(async () => {
    if (testingAll) {return;}
    if (!connections.length) {
      message.info('No connections to test.');
      return;
    }
    setTestingAll(true);
    const failures = [];
    const hide = message.loading(`Testing ${connections.length} connections…`, 0);
    try {
      for (const c of connections) {
        try {
          await testConnection(c.id);
        } catch (err) {
          failures.push(`${c.name || c.connector_name}: ${err.message}`);
        }
      }
      await refresh({ silent: true, runsOnly: true });
      const ok = connections.length - failures.length;
      if (failures.length === 0) {
        message.success(`Tested ${ok} connection${ok === 1 ? '' : 's'} \u2014 all healthy.`);
      } else {
        message.warning(
          `Tested ${connections.length}: ${ok} ok, ${failures.length} failed. First error: ${failures[0]}`,
        );
      }
    } finally {
      hide();
      setTestingAll(false);
    }
  }, [connections, refresh, testingAll]);

  // ── Bulk: Trigger a sync for every existing connection ───────────────
  // Each call just queues a Dagster run, so it returns quickly even when
  // the actual extracts take a while; the right-pane will pick up the
  // running runs via the existing 5-second poll.
  const handleSyncAll = useCallback(async () => {
    if (syncingAll) {return;}
    if (!connections.length) {
      message.info('No connections to sync.');
      return;
    }
    setSyncingAll(true);
    // Mark every card as "Sending ingestion request…" up front so the
    // user sees feedback on each tile, not just the bulk button.
    for (const c of connections) {markPendingSync(c.id);}
    const failures = [];
    const hide = message.loading(`Triggering sync for ${connections.length} connections…`, 0);
    try {
      for (const c of connections) {
        try {
          await triggerSync(c.id);
        } catch (err) {
          failures.push(`${c.name || c.connector_name}: ${err.message}`);
          clearPendingSync(c.id);
        }
      }
      await refresh({ silent: true, runsOnly: true });
      const ok = connections.length - failures.length;
      if (failures.length === 0) {
        message.success(`Sync triggered for ${ok} connection${ok === 1 ? '' : 's'}.`);
      } else {
        message.warning(
          `Triggered ${ok} of ${connections.length}. First error: ${failures[0]}`,
        );
      }
    } finally {
      hide();
      setSyncingAll(false);
    }
  }, [connections, refresh, syncingAll, markPendingSync, clearPendingSync]);

  // ── Bulk: UAT-only "Sync all — UAT" ──────────────────────────────────
  // Same fan-out as handleSyncAll but with use_saved_samples=true so
  // the orchestrator replays cached Test Connection samples into the
  // bronze tables instead of calling the live sources. Lets QA validate
  // the bronze → dbt → silver pipeline without burning API quota.
  const handleSyncAllUat = useCallback(async () => {
    if (syncingAllUat) {return;}
    if (!connections.length) {
      message.info('No connections to sync.');
      return;
    }
    setSyncingAllUat(true);
    // Pre-mark each card so the "Sending ingestion request…" pill
    // shows on every tile while the bulk request fans out.
    for (const c of connections) {markPendingSync(c.id);}
    const failures = [];
    const hide = message.loading(
      `Triggering UAT sync for ${connections.length} connections (${bulkUatSampleLimit} row${bulkUatSampleLimit === 1 ? '' : 's'} per stream)…`,
      0,
    );
    try {
      for (const c of connections) {
        try {
          await triggerSync(c.id, null, {
            useSaved: true,
            sampleLimit: bulkUatSampleLimit,
          });
        } catch (err) {
          failures.push(`${c.name || c.connector_name}: ${err.message}`);
          clearPendingSync(c.id);
        }
      }
      await refresh({ silent: true, runsOnly: true });
      const ok = connections.length - failures.length;
      if (failures.length === 0) {
        message.success(`UAT sync triggered for ${ok} connection${ok === 1 ? '' : 's'}.`);
      } else {
        message.warning(
          `Triggered ${ok} of ${connections.length}. First error: ${failures[0]}`,
        );
      }
    } finally {
      hide();
      setSyncingAllUat(false);
    }
  }, [connections, refresh, syncingAllUat, bulkUatSampleLimit, markPendingSync, clearPendingSync]);

  // A run is "active" (and the page should keep polling) when EITHER
  // staging is still in flight OR the bronze transform run is still
  // pending/running. Without checking phase, the poll stopped the
  // moment staging flipped to 'success' — so the card would freeze on
  // "Source data synced — preparing transform" until the user
  // hard-refreshed, even though the bronze sensor was working in the
  // background.
  const hasActive = useMemo(
    () => allRuns.some((r) => {
      if (r.status === 'queued' || r.status === 'running') {return true;}
      return r.phase === PHASE.EXTRACTED || r.phase === PHASE.TRANSFORMING;
    }),
    [allRuns],
  );
  useEffect(() => {
    // Always auto-refresh — Dagster-style. While a sync is in flight
    // (or a trigger has just been fired but no run is visible yet) we
    // poll fast so cards reflect live status; otherwise we tick at a
    // slower idle rate so newly-arriving runs from background
    // schedules / sensors still surface without a manual refresh, and
    // the countdown indicator never freezes.
    const ACTIVE_REFRESH_MS = 5000;
    const IDLE_REFRESH_MS = 10000;
    const hasPendingTrigger = Object.keys(syncingByConn).length > 0;
    const intervalMs = (hasActive || hasPendingTrigger)
      ? ACTIVE_REFRESH_MS
      : IDLE_REFRESH_MS;
    setNextRefreshAt(Date.now() + intervalMs);
    const t = setInterval(() => {
      refresh({ silent: true, runsOnly: true });
      setNextRefreshAt(Date.now() + intervalMs);
    }, intervalMs);
    return () => clearInterval(t);
  }, [hasActive, refresh, syncingByConn]);

  const handleManualRefresh = useCallback(() => {
    refresh({ silent: true });
    setNextRefreshAt(Date.now() + MANUAL_REFRESH_WINDOW_MS);
  }, [refresh]);

  // One connection per (workspace, connector_name) — straight lookup.
  const connectionBySource = useMemo(() => {
    const m = {};
    for (const c of connections) {m[c.connector_name] = c;}
    return m;
  }, [connections]);

  // Latest sync run per connection. `allRuns` is already sorted desc by
  // started_at on the backend, so the first hit per connection_id wins.
  const latestRunByConn = useMemo(() => {
    const m = {};
    for (const r of allRuns) {
      if (!m[r.connection_id]) {m[r.connection_id] = r;}
    }
    return m;
  }, [allRuns]);

  const triggerSyncFromCard = useCallback(async (connection) => {
    if (!connection) {return;}
    // Stamp the trigger time so the card shows "Sending ingestion
    // request…" until a fresh Dagster run shows up in `allRuns`.
    // Without this, the card's status sat on the *previous* run's
    // terminal state (e.g. "Success") for several seconds between
    // click and run appearance.
    markPendingSync(connection.id);
    try {
      await triggerSync(connection.id);
      message.success(`Sync triggered for ${connection.name}`);
      await refresh({ silent: true, runsOnly: true });
    } catch (e) {
      message.error(`Sync failed: ${e.message}`);
      clearPendingSync(connection.id);
    }
    // NOTE: success path does NOT clear here. The reconcile effect
    // below clears it once a matching run becomes visible (or after
    // the 30s safety timeout) so the indicator stays on through the
    // API → Dagster gap.
  }, [refresh, markPendingSync, clearPendingSync]);

  // Clear pending-trigger flags once a fresh run is visible. The run
  // is "fresh" when its started_at is at-or-after the click timestamp.
  // We also gate on a 30s safety net so a permanently-missing run
  // (e.g. Dagster down) doesn't pin "Sending…" forever.
  //
  // We enforce a 1.2s minimum display time so users actually see the
  // "Sending ingestion request…" tag — without it the Dagster run row
  // typically appears within ~200ms of the trigger and the tag flashes
  // away before anyone notices.
  const MIN_SENDING_DISPLAY_MS = 1200;
  useEffect(() => {
    if (Object.keys(syncingByConn).length === 0) {return undefined;}
    const tick = () => {
      setSyncingByConn((prev) => {
        let changed = false;
        const next = { ...prev };
        const now = Date.now();
        for (const [connId, ts] of Object.entries(prev)) {
          if (now - ts < MIN_SENDING_DISPLAY_MS) {continue;}
          const newest = newestRunStartedAtMs(allRuns, connId);
          // Allow a 2s clock-skew tolerance vs the trigger stamp.
          if (newest >= ts - SENDING_CLOCK_SKEW_MS || now - ts > SENDING_MAX_DISPLAY_MS) {
            delete next[connId];
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    };
    tick();
    // Re-evaluate after the minimum-display window elapses, in case a
    // Dagster run appeared during it.
    const t = setTimeout(tick, MIN_SENDING_DISPLAY_MS);
    return () => clearTimeout(t);
  }, [allRuns, syncingByConn]);

  const selectedConnection = resolveSelectedConnection(sourceName, connectionBySource);
  const selectedSpec = useMemo(
    () => specs.find((s) => s.name === sourceName) || null,
    [specs, sourceName],
  );

  const selectedRuns = useMemo(
    () => (selectedConnection ? allRuns.filter((r) => r.connection_id === selectedConnection.id) : []),
    [allRuns, selectedConnection],
  );

  // Anchor the per-run poller on the latest run id+status only — not on
  // the whole `selectedRuns` array reference. Otherwise the effect tears
  // down and restarts on every parent render (because `selectedRuns` is
  // a fresh array each time), restarting the 1.5s timer mid-fetch and
  // keeping a flicker visible on the progress row.
  const {
    latestRunId, latestRunStatus, latestRunPhase, selectedConnId, fallbackKey,
  } = latestRunProjection(selectedRuns, selectedConnection);
  useEffect(() => {
    setLatestRunDetail(null);
    if (!selectedConnId || !latestRunId) {return undefined;}
    let cancel = false;
    const fallbackStreamNames = fallbackKey ? fallbackKey.split(',') : [];
    const fetchOnce = () => getSyncRun(latestRunId, { fallbackStreamNames })
      .then((d) => { if (!cancel) {setLatestRunDetail(d);} })
      .catch(() => {});
    fetchOnce();
    // Poll every 1.5s while the run is active so the per-stream
    // rows_loaded / requests_made progress bars tick visibly. Faster
    // than the global 5s refresh because progress UX needs to feel live.
    //
    // "Active" includes the bronze transform phases: even after staging
    // flips to 'success', the bronze run may still be queued or
    // running, and the user wants to see "Transforming data → Done"
    // tick through. Without phase here, the timer stopped at staging
    // success and the detail view froze until manual refresh.
    let timer;
    const detailActive = latestRunStatus === 'queued'
      || latestRunStatus === 'running'
      || latestRunPhase === PHASE.EXTRACTED
      || latestRunPhase === PHASE.TRANSFORMING;
    if (detailActive) {
      timer = setInterval(fetchOnce, RUN_DETAIL_POLL_MS);
    }
    return () => { cancel = true; if (timer) {clearInterval(timer);} };
  }, [selectedConnId, latestRunId, latestRunStatus, latestRunPhase, fallbackKey]);

  // Backfill detail for the recent non-latest runs of the selected
  // connection. Encoded as a stable signature so re-renders that don't
  // change the relevant set of runs don't re-trigger the fetch loop.
  const extraRunsSignature = useMemo(() => {
    return selectedRuns
      .slice(1, 10)
      .map((r) => `${r.id}:${r.status}:${r.phase || ''}`)
      .join('|');
  }, [selectedRuns]);
  useEffect(() => {
    setExtraRunDetails([]);
    if (!selectedConnId) {return undefined;}
    const ids = selectedRuns.slice(1, 10).map((r) => r.id);
    if (ids.length === 0) {return undefined;}
    let cancel = false;
    const fallbackStreamNames = fallbackKey ? fallbackKey.split(',') : [];
    const fetchAll = () => fetchManyRunDetails(ids, fallbackStreamNames).then((arr) => {
      if (cancel) {return;}
      setExtraRunDetails(arr.filter(Boolean));
    });
    fetchAll();
    const hasActiveExtra = selectedRuns.slice(1, 10).some((r) =>
      r.status === 'queued' || r.status === 'running'
      || r.phase === PHASE.EXTRACTED || r.phase === PHASE.TRANSFORMING,
    );
    let timer;
    if (hasActiveExtra) {timer = setInterval(fetchAll, RUN_DETAIL_POLL_MS);}
    return () => { cancel = true; if (timer) {clearInterval(timer);} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnId, extraRunsSignature, fallbackKey]);

  const setUrl = useCallback((patch) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') {next.delete(k);} else {next.set(k, v);}
    }
    setParams(next, { replace: false });
  }, [params, setParams]);

  // Click on a tile = SELECT only (no auto-open of the create form).
  // Clearing `connect` makes the disconnected-source preview the default
  // for an unconnected pick; the user opts into the form via the Connect CTA.
  const onPickSource = useCallback((spec) => {
    setUrl({ source: spec.name, stream: '', edit: '', connect: '' });
  }, [setUrl]);

  // Filtered + grouped catalog by category.
  const groupedSpecs = useMemo(() => {
    const q = search.trim().toLowerCase();
    let filtered = !q ? specs : specs.filter((s) => {
      const blob = [s.display_name, s.name, s.description, s.category, ...(s.tags || [])]
        .filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
    if (showOnlyConnected) {
      filtered = filtered.filter((s) => !!connectionBySource[s.name]);
    }
    if (showOnlyAvailable) {
      filtered = filtered.filter((s) => availability[s.name]?.state !== UNAVAILABLE);
    }
    const groups = {};
    for (const s of filtered) {
      const cat = s.category || 'other';
      groups[cat] ||= [];
      groups[cat].push(s);
    }
    for (const cat of Object.keys(groups)) {
      groups[cat].sort((a, b) => {
        const ca = connectionBySource[a.name] ? 0 : 1;
        const cb = connectionBySource[b.name] ? 0 : 1;
        if (ca !== cb) {return ca - cb;}
        return (a.display_name || a.name).localeCompare(b.display_name || b.name);
      });
    }
    const ordered = [];
    for (const cat of CATEGORY_ORDER) {
      if (groups[cat]) { ordered.push([cat, groups[cat]]); delete groups[cat]; }
    }
    for (const cat of Object.keys(groups).sort((a, b) => a.localeCompare(b))) {
      ordered.push([cat, groups[cat]]);
    }
    return ordered;
  }, [specs, search, connectionBySource, showOnlyConnected, showOnlyAvailable, availability]);

  if (loading) {
    return <div style={{ padding: 64, textAlign: 'center' }}><Spin size="large" /></div>;
  }

  // Decide what the right pane shows.
  const isConnected = !!selectedConnection;
  // For disconnected sources: default to a read-only preview (description +
  // tables). User hits the "Connect" CTA in the preview to reveal the form.
  // ?connect=1 forces the form directly (deep links from the catalog).
  const wantsConnectForm = params.get('connect') === '1';
  const { showCreateForm, showDisconnectedPreview } = resolveRightPaneMode({
    hasSpec: !!selectedSpec, isConnected, wantsConnectForm,
  });

  return (
    <div style={{ height: 'calc(100vh - 20px)', overflow: 'hidden', position: 'relative' }}>
      <RefreshCountdown
        nextRefreshAt={nextRefreshAt}
        onRefresh={handleManualRefresh}
      />
      <Row style={{ height: '100%' }} gutter={0}>
        {/* ─────── LEFT — categorized connection grid (1/3) ─────── */}
        <Col span={8} style={{
          height: '100%',
          borderRight: CARD_BORDER,
          display: 'flex',
          flexDirection: 'column',
          background: THEME_CONTENT_BG,
        }}>
          <div style={{ padding: '16px 20px', borderBottom: CARD_BORDER, background: THEME_CONTENT_BG }}>
            <BulkActions
              connections={connections} uatMode={uatMode}
              testingAll={testingAll} handleTestAll={handleTestAll}
              syncingAll={syncingAll} handleSyncAll={handleSyncAll}
              syncingAllUat={syncingAllUat} handleSyncAllUat={handleSyncAllUat}
              bulkUatSampleLimit={bulkUatSampleLimit} setBulkUatSampleLimit={setBulkUatSampleLimit}
            />
            {/* The orchestrator is scaled to zero in cost-reduced environments,
                which is a configuration choice, not a fault. Say so: without
                this the page shows an empty sync history and dead schedule
                controls with no explanation, and the API used to answer 502 on
                every poll — six e2e tests failed on a deployment that was
                behaving exactly as configured. */}
            {orchestratorAbsent && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 8 }}
                title="Scheduled ingestion is off in this environment"
                description={'The data-ingestion orchestrator is not running here, so there is no '
                  + 'sync history and schedules cannot fire. Connecting and testing sources still '
                  + 'works; triggering a sync does not.'}
              />
            )}
            <PipelineTools
              orchestratorAbsent={orchestratorAbsent} setSyncHistoryOpen={setSyncHistoryOpen}
              setScheduleTimelineOpen={setScheduleTimelineOpen} handleExportTemplate={handleExportTemplate}
              importBusy={importBusy} importInputRef={importInputRef} onPickImportFile={onPickImportFile}
            />
            <Input
              allowClear
              placeholder="Search connections…"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ marginTop: 12 }}
            />
            <div style={{ marginTop: 8 }}>
              <Space size={12} wrap>
                <Checkbox
                  checked={showOnlyConnected}
                  onChange={(e) => setShowOnlyConnected(e.target.checked)}
                >
                  <Text style={{ fontSize: 12 }}>
                    Show only connected
                  </Text>
                </Checkbox>
                <Checkbox
                  checked={showOnlyAvailable}
                  onChange={(e) => setShowOnlyAvailable(e.target.checked)}
                >
                  <Text style={{ fontSize: 12 }}>
                    Show only available
                  </Text>
                </Checkbox>
              </Space>
            </div>
          </div>

          <div style={{ flex: 1, overflow: 'auto', padding: '8px 16px 16px' }}>
            {groupedSpecs.length === 0 ? (
              <Empty description="No connections match." style={{ marginTop: 64 }} />
            ) : (
              groupedSpecs.map(([cat, list]) => {
                const connectedCount = list.filter((s) => !!connectionBySource[s.name]).length;
                return (
                  <CategorySection
                    key={cat}
                    label={categoryLabel(cat)}
                    connectedCount={connectedCount}
                    total={list.length}
                  >
                    <Row gutter={[12, 12]}>
                      {list.map((spec) => {
                        const conn = connectionBySource[spec.name] || null;
                        const avail = availability[spec.name];
                        // If the user just connected but Dagster hasn't yet
                        // picked up the new workspace.yaml, /availability
                        // omits this connector. Surface that race as LOADING
                        // so the UI mirrors the actual Dagster status.
                        const dagsterState = avail?.state
                          || (conn ? 'LOADING' : null);
                        // Block sync only when Dagster says the code
                        // location is in a state where a run can't launch.
                        // LOADING happens for ~30-60s after a connect.
                        const isUnavailable =
                          dagsterState === UNAVAILABLE ||
                          dagsterState === 'LOADING' ||
                          dagsterState === 'ERROR';
                        return (
                          <Col xs={24} key={spec.name}>
                            <ConnectionCard
                              spec={spec}
                              connection={conn}
                              latestRun={conn ? latestRunByConn[conn.id] : null}
                              syncing={!!(conn && syncingByConn[conn.id])}
                              selected={sourceName === spec.name}
                              onClick={() => onPickSource(spec)}
                              onSync={() => triggerSyncFromCard(conn)}
                              unavailable={isUnavailable}
                              unavailableMessage={avail?.message}
                              dagsterState={dagsterState}
                              schedulerSensor={schedulerByConnector[connectorSensorKey(spec.name)]}
                              schedulerDaemonHealthy={schedulerDaemonHealthy}
                              onSchedulerChanged={setSchedulerStatus}
                            />
                          </Col>
                        );
                      })}
                    </Row>
                  </CategorySection>
                );
              })
            )}
            <TrademarkNotice specs={specs} />
          </div>
        </Col>

        {/* ─────── RIGHT — config + tables, OR create form (2/3) ─────── */}
        <Col span={16} style={{ height: '100%', overflow: 'auto', background: THEME_CONTENT_BG }}>
          <RightPane
            selectedSpec={selectedSpec} selectedConnection={selectedConnection}
            showDisconnectedPreview={showDisconnectedPreview} showCreateForm={showCreateForm}
            setUrl={setUrl} refresh={refresh} editing={editing}
            latestRunDetail={latestRunDetail} extraRunDetails={extraRunDetails} selectedRuns={selectedRuns}
            schedulerByConnector={schedulerByConnector} schedulerDaemonHealthy={schedulerDaemonHealthy}
            setSchedulerStatus={setSchedulerStatus} syncingByConn={syncingByConn} markPendingSync={markPendingSync}
          />
        </Col>
      </Row>

      {/* Stream detail drawer (only sliding window kept) */}
      <StreamDetailDrawer
        open={!!streamName && !!selectedConnection}
        connection={selectedConnection}
        spec={selectedSpec}
        streamName={streamName}
        onChange={() => refresh({ silent: true })}
        onClose={() => setUrl({ stream: '' })}
      />

      {/* Bulk-import result modal */}
      <ImportResultModal
        result={importResult}
        onClose={() => setImportResult(null)}
      />

      {/* Sync history modal — Dagster-backed, scoped to the workspace.
          Opened from the LEFT-pane "Sync history" button, so the
          connection filter is intentionally NOT pre-bound to the
          currently selected source: users can switch connections from
          here. The per-source variant lives inside ConnectedSourceView
          and passes `connectionId` to lock the picker. */}
      <SyncHistoryDialog
        open={syncHistoryOpen}
        onClose={() => setSyncHistoryOpen(false)}
      />

      {/* Schedule timeline — full-screen, workspace-wide projection of every
          connection's cron schedule overlaid with actual runs. */}
      <ScheduleTimelineDialog
        open={scheduleTimelineOpen}
        onClose={() => setScheduleTimelineOpen(false)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Bulk-import result modal — shown after POST /connections/import
// ─────────────────────────────────────────────────────────────
function ImportResultModal({ result, onClose }) {
  if (!result) {return null;}
  const columns = [
    {
      title: 'Connector',
      dataIndex: 'connector_name',
      render: (v, r) => (
        <span>
          <Text strong>{v}</Text>{' '}
          <Text type="secondary" style={{ fontSize: 11 }}>{r.connector_version}</Text>
          {r.name ? <div style={{ fontSize: 11, color: '#888' }}>{r.name}</div> : null}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s) => {
        const map = {
          created: { color: 'green', icon: <CheckCircleFilled /> },
          updated: { color: 'blue', icon: <SyncOutlined /> },
          skipped: { color: 'default', icon: <ClockCircleFilled /> },
          failed: { color: 'red', icon: <CloseCircleFilled /> },
        };
        const m = map[s] || {};
        return <Tag color={m.color} icon={m.icon}>{s}</Tag>;
      },
    },
    {
      title: 'Healthy',
      dataIndex: 'healthy',
      width: 90,
      render: renderHealthyTag,
    },
    {
      title: 'Detail',
      dataIndex: 'error',
      render: (err, r) => {
        if (err) {return <Text type="danger" style={{ fontSize: 12 }}>{err}</Text>;}
        if (r.stream_test_result) {
          const entries = Object.entries(r.stream_test_result)
            .filter(([k]) => k !== '_error');
          return (
            <Space size={4} wrap>
              {entries.map(([name, h]) => (
                <Tooltip key={name} title={h.error || `${h.total_records ?? '?'} records`}>
                  <Tag color={h.healthy ? 'green' : 'red'} style={{ fontSize: 11 }}>
                    {name}
                  </Tag>
                </Tooltip>
              ))}
            </Space>
          );
        }
        return null;
      },
    },
  ];
  return (
    <Modal
      open
      onCancel={onClose}
      onOk={onClose}
      okText="Done"
      cancelButtonProps={{ style: { display: 'none' } }}
      width={820}
      title="Import results"
    >
      <Space size={16} style={{ marginBottom: 12 }}>
        <Statistic title="Created" value={result.created} styles={{ content: { color: '#3f8600' } }} />
        <Statistic title="Updated" value={result.updated} styles={{ content: { color: '#1677ff' } }} />
        <Statistic title="Skipped" value={result.skipped} />
        <Statistic title="Failed"  value={result.failed}  styles={{ content: { color: result.failed ? '#cf1322' : undefined } }} />
      </Space>
      <Table
        size="small"
        rowKey={(r, i) => `${r.connector_name}-${i}`}
        dataSource={result.entries}
        columns={columns}
        pagination={false}
        scroll={{ y: 360 }}
      />
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
// Category section
// ─────────────────────────────────────────────────────────────
function CategorySection({ label, connectedCount, total, children }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8,
        padding: '4px 4px 8px', marginBottom: 4,
      }}>
        <Text strong style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {connectedCount > 0 ? `${connectedCount}/${total} connected` : `${total} available`}
        </Text>
      </div>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Connection tile — full-width row showing status, last-sync,
// stream-health summary, and an inline Sync button so operators
// can monitor and trigger every connection without drilling in.
// ─────────────────────────────────────────────────────────────
// Platform-level availability (Dagster code location stopped/unloaded)
// overrides whatever the run history says.
export function resolveCardUnavailability({ unavailable, spec, unavailableMessage }) {
  const isUnavailable = typeof unavailable === 'boolean'
    ? unavailable
    : spec?.availability?.state === UNAVAILABLE;
  const tooltip = unavailableMessage
    || spec?.availability?.message
    || 'This connector is temporarily unavailable. Our team has been notified — no action needed.';
  return { isUnavailable, tooltip };
}

// Per-stream "Healthy" cell: dash when unknown, else OK / FAIL tag.
function renderHealthyTag(v) {
  if (v == null) {return <Text type="secondary">—</Text>;}
  if (v) {return <Tag color="green" icon={<CheckCircleFilled />}>OK</Tag>;}
  return <Tag color="red" icon={<CloseCircleFilled />}>FAIL</Tag>;
}

// Sync-mode tag for a stream in the catalog list.
function streamModeTag(s) {
  if (s.is_snapshot) {return <Tag color="default" style={{ fontSize: 10 }}>snapshot</Tag>;}
  if (s.cursor_field) {return <Tag color="blue" style={{ fontSize: 10 }}>incremental</Tag>;}
  return <Tag color="default" style={{ fontSize: 10 }}>full</Tag>;
}

// Status tag for a tri-state ceiling check (true / false / null = not checked).
function ceilingStatusTag(ok) {
  if (ok === true) {return <Tag color="success" style={{ margin: 0 }}>ok</Tag>;}
  if (ok === false) {return <Tag color="error" style={{ margin: 0 }}>failed</Tag>;}
  return <Tag style={{ margin: 0 }}>not checked</Tag>;
}

// Select option label for a per-stream load mode.
function loadModeOptionLabel(o, connection) {
  if (o.value !== INHERIT) {return o.label;}
  const inherited = connection.load_mode === INCREMENTAL ? 'Delta' : 'Full';
  return `Inherit (${inherited})`;
}

// Card border color + shadow: selection takes priority, then connected
// (green, or red when the last run failed), then the disconnected default.
export function resolveCardBorder({ selected, connected, runStatus }) {
  let borderColor;
  if (selected) {
    borderColor = 'var(--theme-primary)';
  } else if (connected) {
    borderColor = runStatus === 'failed' ? '#ffa39e' : '#b7eb8f';
  } else {
    borderColor = 'var(--theme-border, #e8e8e8)';
  }
  const baseShadow = '0 1px 2px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.04)';
  const shadow = selected
    ? '0 0 0 2px var(--theme-shadowStrong), 0 4px 12px rgba(0,0,0,0.08)'
    : baseShadow;
  return { borderColor, shadow };
}

// Stream-test rollup so the card can say "3 healthy · 1 error" at a
// glance, drawn from the same dict that powers the right pane.
export function computeHealthSummary(connection) {
  const sh = connection?.stream_test_result;
  if (!sh) {return null;}
  const entries = Object.entries(sh).filter(([k]) => k !== '_error');
  if (entries.length === 0) {return null;}
  let healthy = 0;
  let unhealthy = 0;
  for (const [, v] of entries) {
    if (v && typeof v === 'object') {
      if (v.healthy) {healthy += 1;}
      else if (v.healthy === false) {unhealthy += 1;}
      else { /* v.healthy is neither true nor false (e.g. unset) — not counted either way */ }
    }
  }
  return { total: entries.length, healthy, unhealthy };
}

// Dagster code-location load-state badge for an unavailable card: which
// label/tooltip/color to show for LOADING vs. ERROR vs. the legacy
// "temporarily unavailable" case.
export function resolveDagsterStateBadge({ dagsterState, unavailableMessage }) {
  const isLoading = dagsterState === 'LOADING';
  const isError = dagsterState === 'ERROR';
  let label;
  if (isLoading) {
    label = 'Loading in Dagster…';
  } else if (isError) {
    label = 'Failed to load in Dagster';
  } else {
    label = 'Temporarily unavailable';
  }
  let tooltip;
  if (isLoading) {
    tooltip = "Dagster is importing this connector's code location. The first sync becomes available within ~30-60s.";
  } else if (unavailableMessage) {
    tooltip = unavailableMessage;
  } else if (isError) {
    tooltip = "This connector's code location failed to import in Dagster. Check pipeline-api / orchestrator logs.";
  } else {
    tooltip = 'This connector is temporarily unavailable.';
  }
  // Use AntD's semantic Tag colors so the pastel backgrounds adapt to the
  // active light/dark algorithm instead of being pinned light.
  const tagColor = isError ? 'error' : 'warning';
  return { isLoading, isError, label, tooltip, tagColor };
}

// Dagster code-location load-state badge for an unavailable card's
// bottom-right corner. We surface three flavors:
//   LOADING — orange spinner ("Loading in Dagster…")
//   ERROR   — red ("Failed to load in Dagster")
//   *       — yellow (legacy "Temporarily unavailable")
function DagsterUnavailableBadge({ dagsterState, unavailableMessage }) {
  const { isLoading, label, tooltip, tagColor } = resolveDagsterStateBadge({
    dagsterState, unavailableMessage,
  });
  return (
    <Tooltip title={tooltip}>
      <Tag
        color={tagColor}
        icon={isLoading ? <SyncOutlined spin /> : <StopOutlined />}
        style={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          margin: 0,
          fontSize: 11,
          lineHeight: '18px',
          padding: '0 6px',
        }}
      >
        {label}
      </Tag>
    </Tooltip>
  );
}

// A tile's frame: dashed and dimmed until connected, dimmer still when
// Pro-locked.
function connectionCardStyle({ borderColor, shadow, connected, proLocked }) {
  let opacity = connected ? 1 : CARD_OPACITY_DISCONNECTED;
  if (proLocked) {opacity = CARD_OPACITY_PRO_LOCKED;}
  return {
    position: 'relative',
    border: `1px solid ${borderColor}`,
    borderStyle: connected ? 'solid' : 'dashed',
    borderRadius: 10,
    boxShadow: shadow,
    background: connected ? THEME_CONTENT_BG : 'var(--theme-backgroundSecondary, #f5f5f5)',
    transition: 'all 0.15s ease',
    cursor: 'pointer',
    opacity,
  };
}

// Connectors are named in text only, as "<vendor product> connector": no
// vendor logo is shown anywhere in the console.
export function connectorTitle(spec) {
  return `${spec.display_name || spec.name} connector`;
}

// Notice under the connector list on why vendor product names appear.
export const PRODUCT_NAMES_NOTICE = 'Vendor product names are used only to identify connection '
  + 'compatibility; no affiliation or endorsement is implied.';

function TrademarkNotice({ specs }) {
  if (!specs?.length) {return null;}
  return (
    <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 16, marginBottom: 0 }}>
      {PRODUCT_NAMES_NOTICE}
    </Paragraph>
  );
}

// Name, Pro badge and (connected, unlocked) last-run dot.
function CardTitleLine({ spec, proLocked, connected, runStatus }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
      <Text strong style={{ fontSize: 15 }} ellipsis>
        {connectorTitle(spec)}
      </Text>
      {proLocked && <ProBadge />}
      {connected && !proLocked
        ? <Badge status={runStatus === 'failed' ? 'error' : 'success'} />
        : null}
    </div>
  );
}

// A connected tile's status: the run state and live phase, then timing
// and the per-stream Test Connection rollup.
function ConnectedStatus({ syncing, isActive, runStatus, latestRun, healthSummary }) {
  return (
    <Space orientation="vertical" size={4} style={{ width: '100%' }}>
      {/* Row 1: terminal status + live progress phase. The
          phase ("Waiting in queue / Syncing data from source /
          Transforming data") is the user-facing answer to
          backlog item 2 — make the card communicate what the
          pipeline is actually doing, not just its raw state. */}
      <RunStatusRow syncing={syncing} isActive={isActive} runStatus={runStatus} latestRun={latestRun} />
      {/* Row 2: timing + stream-test rollup */}
      <TimingRow latestRun={latestRun} isActive={isActive} healthSummary={healthSummary} />
    </Space>
  );
}

function RunStatusRow({ syncing, isActive, runStatus, latestRun }) {
  return (
    <Space size={6} wrap>
      {syncing ? (
        <Tag color="processing" icon={<SyncOutlined spin />} style={{ margin: 0 }}>
          Sending ingestion request…
        </Tag>
      ) : (
        <>
          <StatusTag
            status={isActive ? 'running' : runStatus || 'idle'}
            stale={latestRun?.stale}
            tooltip={latestRun?.error}
          />
          {latestRun?.phase && <PhaseTag phase={latestRun.phase} compact />}
        </>
      )}
    </Space>
  );
}

function TimingRow({ latestRun, isActive, healthSummary }) {
  let syncText = 'never synced';
  if (latestRun) {
    const verb = isActive ? 'started' : 'last sync';
    syncText = `${verb} ${timeAgo(latestRun.started_at)}`;
  }
  return (
    <Space size={10} wrap style={{ fontSize: 12, lineHeight: 1.4 }}>
      <Tooltip
        title={latestRun?.finished_at || latestRun?.started_at || 'Never synced'}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          <ClockCircleFilled style={{ marginRight: 4 }} />
          {syncText}
        </Text>
      </Tooltip>
      {healthSummary && <HealthRollup healthSummary={healthSummary} />}
    </Space>
  );
}

function HealthRollup({ healthSummary }) {
  return (
    <Tooltip title="Per-stream Test Connection rollup. Click the tile to see per-stream details.">
      <Text style={{ fontSize: 12 }}>
        <CheckCircleFilled style={{ color: '#52c41a', marginRight: 4 }} />
        {healthSummary.healthy}/{healthSummary.total} healthy
        {healthSummary.unhealthy > 0 && (
          <Text type="danger" style={{ marginLeft: 6 }}>
            <CloseCircleFilled style={{ marginRight: 4 }} />
            {healthSummary.unhealthy} failing
          </Text>
        )}
      </Text>
    </Tooltip>
  );
}

function DisconnectedHint({ proLocked }) {
  if (proLocked) {
    return (
      <Text type="secondary" style={{ fontSize: 13 }}>
        <LockOutlined style={{ marginRight: 4 }} /> Available in Pro — compare plans
      </Text>
    );
  }
  return (
    <Text type="secondary" style={{ fontSize: 13 }}>
      + Connect
    </Text>
  );
}

// The Scheduler Start/Stop and Sync columns (connected tiles only).
function CardActionCols({
  connected, connection, spec, dagsterState, stop,
  schedulerSensor, schedulerDaemonHealthy, onSchedulerChanged,
  isActive, syncing, isUnavailable, onSync,
}) {
  return (
    <>
      {/* Per-connector scheduler Start/Stop. Shown for connected sources so
          the user can enable/pause cron scheduling without leaving the card. */}
      {connected && (
        <Col flex="none" onClick={stop}>
          <SchedulerControl
            connector={connectorSensorKey(connection.connector_name || spec.name)}
            sensor={schedulerSensor}
            daemonHealthy={schedulerDaemonHealthy}
            onChanged={onSchedulerChanged}
          />
        </Col>
      )}

      {/* Sync action — hidden while Dagster is still importing the code
          location (LOADING) or failed to import (ERROR). Surfacing a
          disabled Sync invites users to click it; hiding it makes the
          status badge the only visible affordance. */}
      {connected && dagsterState !== 'LOADING' && dagsterState !== 'ERROR' && (
        <Col flex="none" onClick={stop}>
          <SyncButton isActive={isActive} syncing={syncing} isUnavailable={isUnavailable} onSync={onSync} stop={stop} />
        </Col>
      )}
    </>
  );
}

function SyncButton({ isActive, syncing, isUnavailable, onSync, stop }) {
  return (
    <Button
      size="small"
      type={isActive ? 'default' : 'primary'}
      icon={isActive ? <SyncOutlined spin /> : <ThunderboltOutlined />}
      loading={syncing}
      disabled={isActive || syncing || isUnavailable}
      onClick={(e) => { stop(e); onSync && onSync(); }}
    >
      {isActive ? 'Syncing…' : 'Sync'}
    </Button>
  );
}

function ConnectionCard({
  spec, connection, latestRun, syncing,
  selected, onClick, onSync,
  unavailable,
  unavailableMessage,
  dagsterState,
  schedulerSensor,
  schedulerDaemonHealthy,
  onSchedulerChanged,
}) {
  const connected = !!connection;
  // Pro-only source surfaced (greyed) in a Standard deployment. Clicking opens
  // the Standard-vs-Pro comparison dialog instead of the connect/config pane.
  const proLocked = !!spec.pro_locked;
  const [cmpOpen, setCmpOpen] = useState(false);
  const PRO_HIGHLIGHT = {
    jira: 'jira-rally', rally: 'jira-rally',
    'aws-bedrock': 'bedrock', 'azure-ai-foundry': 'azure-foundry', 'microsoft-copilot': 'm365',
    cursor: 'cursor', codex: 'codex',
  };
  // Platform-level availability (Dagster code location stopped/unloaded)
  // overrides whatever the run history says. The badge is rendered in the
  // card's bottom-right corner; the surrounding card stays clickable so the
  // tenant can still open the drawer and read the status explanation.
  const { isUnavailable } = resolveCardUnavailability({
    unavailable, spec, unavailableMessage,
  });
  const runStatus = latestRun?.status;
  const isActive = runStatus === 'running' || runStatus === 'queued';
  const { borderColor, shadow } = resolveCardBorder({ selected, connected, runStatus });

  // Stream-test rollup so the card can say "3 healthy · 1 error"
  // at a glance, drawn from the same dict that powers the right pane.
  const healthSummary = computeHealthSummary(connection);

  // Don't bubble button clicks up to the card's onClick (which selects
  // the tile and would replace whatever the user is currently viewing
  // in the right pane).
  const stop = (e) => { e.stopPropagation(); };

  return (
    <Card
      hoverable
      onClick={proLocked ? () => setCmpOpen(true) : onClick}
      styles={{ body: { padding: 16 } }}
      style={connectionCardStyle({ borderColor, shadow, connected, proLocked })}
    >
      <Row gutter={14} align="top" wrap={false}>
        {/* Title + status block */}
        <Col flex="auto" style={{ minWidth: 0 }}>
          <CardTitleLine spec={spec} proLocked={proLocked} connected={connected} runStatus={runStatus} />
          {connected ? (
            <ConnectedStatus
              syncing={syncing} isActive={isActive} runStatus={runStatus}
              latestRun={latestRun} healthSummary={healthSummary}
            />
          ) : (
            <DisconnectedHint proLocked={proLocked} />
          )}
        </Col>

        <CardActionCols
          connected={connected} connection={connection} spec={spec} dagsterState={dagsterState} stop={stop}
          schedulerSensor={schedulerSensor} schedulerDaemonHealthy={schedulerDaemonHealthy} onSchedulerChanged={onSchedulerChanged}
          isActive={isActive} syncing={syncing} isUnavailable={isUnavailable} onSync={onSync}
        />
      </Row>
      {isUnavailable && !proLocked && (
        <DagsterUnavailableBadge dagsterState={dagsterState} unavailableMessage={unavailableMessage} />
      )}
      {proLocked && (
        <EditionComparisonModal
          open={cmpOpen}
          onClose={() => setCmpOpen(false)}
          highlight={PRO_HIGHLIGHT[spec.name] || null}
        />
      )}
    </Card>
  );
}

function EmptyRightPane() {
  return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Space orientation="vertical" size={4}>
            <Text strong>Select a connection</Text>
            <Text type="secondary">Pick a tile on the left to view or configure it.</Text>
          </Space>
        }
      />
    </div>
  );
}

function GuideList({ heading, items, ordered }) {
  if (!items?.length) {return null;}
  const ListTag = ordered ? 'ol' : 'ul';
  return (
    <div>
      <Text strong>{heading}</Text>
      <ListTag style={{ margin: '4px 0 0', paddingLeft: 20 }}>
        {items.map((item) => <li key={item}>{item}</li>)}
      </ListTag>
    </div>
  );
}

// "Information" box: how to get the connector's credential and what it
// needs, each point backed by the vendor's own documentation, named under
// Sources as text only (no outbound links). Copy lives in pipeline-api
// catalog/metadata.py, which keeps the page URLs for maintainers.
export function ConnectorSetupGuide({ spec, defaultOpen = true, style }) {
  const guide = spec?.setup_guide;
  if (!guide || !(guide.steps?.length || guide.permissions?.length)) {return null;}
  return (
    <Collapse
      size="small"
      style={{ marginBottom: 16, ...style }}
      defaultActiveKey={defaultOpen ? ['guide'] : []}
      items={[{
        key: 'guide',
        label: (
          <Space size={6}>
            <InfoCircleOutlined style={{ color: '#1677ff' }} />
            <Text strong>Information: how to get the credentials</Text>
          </Space>
        ),
        children: (
          <Space orientation="vertical" size={10} style={{ width: '100%' }}>
            <GuideList heading="Steps" items={guide.steps} ordered />
            <GuideList heading="Permissions needed" items={guide.permissions} />
            <GuideList heading="Good to know" items={guide.notes} />
            {guide.sources?.length > 0 && (
              <div>
                <Text strong>Sources (official documentation)</Text>
                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                  {guide.sources.map((s) => <li key={s.label}>{s.label}</li>)}
                </ul>
                {guide.checked_on && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Checked against these pages on {guide.checked_on}. Vendors change their
                    screens; if a step looks different, the vendor&apos;s page is the authority.
                  </Text>
                )}
              </div>
            )}
          </Space>
        ),
      }]}
    />
  );
}

// Disconnected-source preview. Shown when the user picks a catalog tile
// that doesn't yet have a Connection row. Surfaces description + table
// list so users can decide what to expect from this connector BEFORE
// they fill in credentials. The Connect CTA flips the right pane to the
// ConnectionForm (via the `connect=1` URL param).
function DisconnectedSourcePreview({ spec, onConnect }) {
  const streams = useMemo(() => spec?.streams || [], [spec]);
  return (
    <div style={{ padding: 24 }}>
      <Row align="middle" gutter={12} style={{ marginBottom: 16 }}>
        <Col flex="auto" style={{ minWidth: 0 }}>
          <Title level={3} style={{ margin: 0 }}>{connectorTitle(spec)}</Title>
          <Space size={6} style={{ marginTop: 4 }}>
            {spec.category && <Tag>{categoryLabel(spec.category)}</Tag>}
            <Text type="secondary" style={{ fontSize: 12 }}>v{spec.version}</Text>
            {spec.docs_url && (
              <a href={spec.docs_url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                Docs <ExportOutlined style={{ fontSize: 11 }} />
              </a>
            )}
          </Space>
        </Col>
        <Col flex="none">
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={onConnect}
          >
            Connect
          </Button>
        </Col>
      </Row>

      {spec.description && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
            {spec.description}
          </Paragraph>
        </Card>
      )}

      <ConnectorSetupGuide spec={spec} />

      <Card
        size="small"
        title={
          <Space>
            <span>Tables this connector ingests</span>
            <Text type="secondary" style={{ fontSize: 12 }}>{streams.length} total</Text>
          </Space>
        }
      >
        {streams.length === 0 ? (
          <Text type="secondary">No streams declared by this connector.</Text>
        ) : (
          <Row gutter={[12, 12]}>
            {streams.map((s) => (
              <Col xs={24} md={12} key={s.name}>
                <div style={{
                  border: CARD_BORDER,
                  borderRadius: 6,
                  padding: '10px 12px',
                  height: '100%',
                }}>
                  <Space size={6} style={{ marginBottom: 4 }}>
                    <code style={{ fontWeight: 600 }}>{s.name}</code>
                    {streamModeTag(s)}
                  </Space>
                  {s.api_endpoint && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>Endpoint: </Text>
                      <code style={{ fontSize: 11, wordBreak: 'break-all' }}>
                        {s.api_endpoint}
                      </code>
                    </div>
                  )}
                  {s.primary_key?.length > 0 && (
                    <div style={{ marginTop: 2 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Primary key: </Text>
                      <code style={{ fontSize: 11 }}>{s.primary_key.join(', ')}</code>
                    </div>
                  )}
                  {s.cursor_field && (
                    <div style={{ marginTop: 2 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Cursor: </Text>
                      <code style={{ fontSize: 11 }}>{s.cursor_field}</code>
                    </div>
                  )}
                  {s.rate_limit && (
                    <div style={{ marginTop: 2 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Rate limit: </Text>
                      <Text style={{ fontSize: 11 }}>{s.rate_limit}</Text>
                    </div>
                  )}
                </div>
              </Col>
            ))}
          </Row>
        )}
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// StreamConnectionTab — "Connection" tab inside the per-stream
// drawer. Renders detail SPECIFIC to the clicked table, falling
// back to connector-level defaults where the stream did not
// declare its own value.
//
//   Per-stream (from Stream dataclass fields):
//     • api_endpoint       — actual call this table pulls from
//     • page_size          — request size used by the extractor
//     • extract_params     — param → description map
//     • rate_limit         — vendor-imposed cap (human readable)
//     • docs_url           — overrides spec.docs_url for this table
//
//   Plus the stream-shape info we already had:
//     • primary_key, cursor_field, is_snapshot, references, requires
//
//   Connector-level context kept for reference at the bottom:
//     • connector name + version, category
//     • required / optional CONNECTION config fields (from JSON schema)
// ─────────────────────────────────────────────────────────────
// Whether a stream's persisted ingestion override (page_size /
// extract_params / rate_limit) carries any real value.
export function hasStreamOverride(persistedOverride) {
  return !!(
    persistedOverride.page_size != null
    || Object.keys(persistedOverride.extract_params || {}).length > 0
    || (persistedOverride.rate_limit || '').trim()
  );
}

// Split a connector's JSON-Schema config fields into required/optional,
// in declaration order.
export function resolveConfigFieldGroups(schema) {
  const props = schema.properties || {};
  const required = new Set(schema.required || []);
  const fieldNames = Object.keys(props);
  return {
    requiredFields: fieldNames.filter((n) => required.has(n)),
    optionalFields: fieldNames.filter((n) => !required.has(n)),
  };
}

// Build display rows for the read-only "Request params" section: one row
// per declared/overridden param key, expanded into one row per value for
// repeated query params (keys ending in `[]`, e.g. `group_by[]`) so the
// UI mirrors the actual wire format.
export function buildParamDisplayRows({ paramKeys, overrideParams, declaredParamDefaults, declaredParams }) {
  return paramKeys.flatMap((n) => {
    const isOverridden = Object.prototype.hasOwnProperty.call(overrideParams, n);
    const literalDefault = declaredParamDefaults[n];
    const rawValue = isOverridden ? overrideParams[n] : (literalDefault ?? declaredParams[n]);
    const desc = declaredParams[n];
    const isArrayParam = n.endsWith('[]') && (literalDefault != null || isOverridden);
    const values = isArrayParam
      ? String(rawValue || '').split(',').map((v) => v.trim()).filter(Boolean)
      : [rawValue];
    return values.map((value, i) => ({
      key: n,
      value,
      isOverridden,
      desc,
      showMuted: !(literalDefault || isOverridden),
      showOverrideTag: isOverridden && i === 0,
    }));
  });
}

// A stream's effective request shape: the tenant override wins over the
// connector-declared default.
function effectiveStreamRequest(stream, persistedOverride) {
  const effectivePageSize = persistedOverride.page_size ?? stream.page_size ?? null;
  const effectiveRateLimit = persistedOverride.rate_limit || stream.rate_limit || null;
  const declaredParams = stream.extract_params || {};
  const declaredParamDefaults = stream.extract_param_defaults || {};
  const overrideParams = persistedOverride.extract_params || {};
  const effectiveParamKeys = Array.from(new Set([
    ...Object.keys(declaredParams), ...Object.keys(overrideParams),
  ]));

  const paramDisplayRows = buildParamDisplayRows({
    paramKeys: effectiveParamKeys, overrideParams, declaredParamDefaults, declaredParams,
  });

  return { effectivePageSize, effectiveRateLimit, effectiveParamKeys, paramDisplayRows };
}

// Per-stream API call detail (read-only).
function ApiCallCard({
  stream, hasOverride, streamDocsUrl, persistedOverride, effectivePageSize,
  effectiveRateLimit, effectiveParamKeys, paramDisplayRows, totalRecords, totalRequests,
}) {
  return (
    <Card
      size="small"
      title={
        <Space size={6}>
          <ApiOutlined />
          <span>API call for <code>{stream.name}</code></span>
          {hasOverride && (
            <Tooltip title="This table has a tenant-specific override on top of the connector defaults. Edit it from the source's 'Tables to sync' section.">
              <Tag color="gold" style={{ marginLeft: 4 }}>overridden</Tag>
            </Tooltip>
          )}
        </Space>
      }
      extra={streamDocsUrl ? (
        <Button
          type="link"
          size="small"
          icon={<ExportOutlined />}
          href={streamDocsUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {stream.docs_url ? 'Table API docs' : 'Connector docs'}
        </Button>
      ) : null}
    >
      <Descriptions column={2} size="small" bordered styles={{ label: { width: 140, fontSize: 12 } }}>
        <Descriptions.Item label="Endpoint" span={2}>
          {stream.api_endpoint
            ? <code style={{ fontSize: 12, wordBreak: 'break-all' }}>{stream.api_endpoint}</code>
            : <Text type="secondary">Not declared by the connector.</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Page size" span={2}>
          <PageSizeValue effectivePageSize={effectivePageSize} persistedOverride={persistedOverride} stream={stream} />
        </Descriptions.Item>
        <Descriptions.Item label="Rate limit" span={2}>
          <RateLimitValue effectiveRateLimit={effectiveRateLimit} persistedOverride={persistedOverride} stream={stream} />
        </Descriptions.Item>
        <Descriptions.Item label="Request params" span={2}>
          <RequestParamsValue effectiveParamKeys={effectiveParamKeys} paramDisplayRows={paramDisplayRows} />
        </Descriptions.Item>
        {(totalRecords != null || totalRequests != null) && (
          <Descriptions.Item label="Last probe" span={2}>
            <LastProbe totalRecords={totalRecords} totalRequests={totalRequests} />
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
}

function PageSizeValue({ effectivePageSize, persistedOverride, stream }) {
  return (
    effectivePageSize != null ? (
      <Space size={6}>
        <Tag color="geekblue">{String(effectivePageSize)} rows/req</Tag>
        {persistedOverride.page_size != null
          && persistedOverride.page_size !== stream.page_size && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            override &middot; connector default {stream.page_size ?? '—'}
          </Text>
        )}
      </Space>
    ) : (
      <Tooltip title="This table does not declare a page size — it may return the full set in a single call (snapshot), or the connector hardcodes paging internally.">
        <Text type="secondary">Not declared</Text>
      </Tooltip>
    )
  );
}

function RateLimitValue({ effectiveRateLimit, persistedOverride, stream }) {
  return (
    effectiveRateLimit ? (
      <Space size={6}>
        <Tag color="orange">{effectiveRateLimit}</Tag>
        {persistedOverride.rate_limit
          && persistedOverride.rate_limit !== stream.rate_limit && (
          <Text type="secondary" style={{ fontSize: 11 }}>override</Text>
        )}
      </Space>
    ) : <Text type="secondary">—</Text>
  );
}

function RequestParamsValue({ effectiveParamKeys, paramDisplayRows }) {
  return (
    effectiveParamKeys.length > 0 ? (
      <Space orientation="vertical" size={4} style={{ width: '100%' }}>
        {paramDisplayRows.map((row, i) => (
          <div key={`${row.key}-${i}`} style={{ fontSize: 12 }}>
            <Tooltip title={row.desc}>
              <Tag
                color={row.isOverridden ? 'gold' : 'blue'}
                style={{ marginRight: 6, cursor: row.desc ? 'help' : 'default' }}
              >
                {row.key}
              </Tag>
            </Tooltip>
            <Text type={row.showMuted ? 'secondary' : undefined}>
              {row.value}
            </Text>
            {row.showOverrideTag && (
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                (override)
              </Text>
            )}
          </div>
        ))}
      </Space>
    ) : <Text type="secondary">No params declared.</Text>
  );
}

function LastProbe({ totalRecords, totalRequests }) {
  return (
    <Space size={12}>
      {totalRecords != null && (
        <span style={{ fontSize: 12 }}>
          <Text type="secondary">Source rows: </Text>
          <Text strong>{totalRecords.toLocaleString()}</Text>
        </span>
      )}
      {totalRequests != null && (
        <span style={{ fontSize: 12 }}>
          <Text type="secondary">Requests/sync: </Text>
          <Text strong>{totalRequests.toLocaleString()}</Text>
        </span>
      )}
    </Space>
  );
}

// Stream shape: PK, cursor, refs, dependencies.
function TableShapeCard({ stream }) {
  const references = stream.references || [];
  const requires = stream.requires || [];
  return (
    <Card
      size="small"
      title={
        <Space size={6}>
          <DatabaseOutlined />
          <span>Table shape</span>
        </Space>
      }
    >
      <Descriptions column={2} size="small" bordered styles={{ label: { width: 140, fontSize: 12 } }}>
        <Descriptions.Item label="Primary key" span={2}>
          {(stream.primary_key || []).length > 0
            ? <code style={{ fontSize: 12 }}>{stream.primary_key.join(', ')}</code>
            : <Text type="secondary">none</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Cursor field">
          {stream.cursor_field
            ? <><code style={{ fontSize: 12 }}>{stream.cursor_field}</code> <Tag color="green" style={{ fontSize: 10 }}>incremental</Tag></>
            : <Text type="secondary">none (full refresh)</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Snapshot">
          {stream.is_snapshot
            ? <Tag color="purple">snapshot — change-aware insert</Tag>
            : <Text type="secondary">no</Text>}
        </Descriptions.Item>
        {references.length > 0 && (
          <Descriptions.Item label="References" span={2}>
            <Space orientation="vertical" size={2}>
              {references.map((r, i) => (
                <Text key={i} style={{ fontSize: 12 }}>
                  <code>{r.column}</code> → <code>{r.target_stream}.{r.target_column}</code>
                </Text>
              ))}
            </Space>
          </Descriptions.Item>
        )}
        {requires.length > 0 && (
          <Descriptions.Item label="Depends on" span={2}>
            <Space size={4} wrap>
              {requires.map((r) => <Tag key={r}><code>{r}</code></Tag>)}
            </Space>
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
}

// Connector-level context: the config schema's required / optional inputs.
function ConnectionInputsCard({ spec }) {
  const schema = spec.config_schema || {};
  const props = schema.properties || {};
  const { requiredFields, optionalFields } = resolveConfigFieldGroups(schema);

  const renderConfigTag = (name) => {
    const p = props[name] || {};
    const isSecret = !!p['x-secret'];
    const def = p.default;
    const hasDefault = def !== undefined && def !== null && def !== '';
    let label = name;
    if (hasDefault) {
      const defText = typeof def === 'object' ? JSON.stringify(def) : String(def);
      label = `${name} = ${defText}`;
    }
    return (
      <Tooltip key={name} title={p.description || (p.type ? `type: ${p.type}` : '')}>
        <Tag color={isSecret ? 'volcano' : undefined} style={{ marginBottom: 4 }}>
          {isSecret && <span style={{ marginRight: 4 }}>🔒</span>}
          {label}
        </Tag>
      </Tooltip>
    );
  };

  return (
    <Card
      size="small"
      title={
        <Space size={6}>
          <InfoCircleOutlined />
          <span>Connection inputs</span>
        </Space>
      }
      extra={
        <Text type="secondary" style={{ fontSize: 11 }}>
          {spec.display_name || spec.name} · v{spec.version}
        </Text>
      }
    >
      <Descriptions column={1} size="small" bordered styles={{ label: { width: 140, fontSize: 12 } }}>
        <Descriptions.Item label="Required">
          {requiredFields.length > 0
            ? <Space wrap size={4}>{requiredFields.map(renderConfigTag)}</Space>
            : <Text type="secondary">None</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Optional">
          {optionalFields.length > 0
            ? <Space wrap size={4}>{optionalFields.map(renderConfigTag)}</Space>
            : <Text type="secondary">None</Text>}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
}

function StreamConnectionTab({ stream, spec, testResult, connection }) {
  const streamDocsUrl = stream.docs_url || spec.docs_url || '';

  // Per-stream tenant override (page_size / extract_params / rate_limit).
  // Edited inside the connection form's "Tables to sync" section, not
  // here — this tab is the read-only viewer.
  const persistedOverride = useMemo(
    () => ((connection?.stream_ingestion_config || {})[stream.name] || {}),
    [connection?.stream_ingestion_config, stream.name],
  );

  const hasOverride = hasStreamOverride(persistedOverride);

  // Effective values = override > connector-declared default.
  const {
    effectivePageSize, effectiveRateLimit, effectiveParamKeys, paramDisplayRows,
  } = effectiveStreamRequest(stream, persistedOverride);
  const totalRequests = testResult?.total_requests;
  const totalRecords = testResult?.total_records;

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      {/* ── Per-stream API call detail (read-only) ─────────────── */}
      <ApiCallCard
        stream={stream} hasOverride={hasOverride} streamDocsUrl={streamDocsUrl}
        persistedOverride={persistedOverride} effectivePageSize={effectivePageSize}
        effectiveRateLimit={effectiveRateLimit} effectiveParamKeys={effectiveParamKeys}
        paramDisplayRows={paramDisplayRows} totalRecords={totalRecords} totalRequests={totalRequests}
      />

      {/* ── Stream shape (PK, cursor, refs, dependencies) ──────── */}
      <TableShapeCard stream={stream} />

      {/* ── Connector-level context (config schema fields) ─────── */}
      <ConnectionInputsCard spec={spec} />
    </Space>
  );
}

// ─────────────────────────────────────────────────────────────
// Connected source view: title bar (with delete), summary card,
// optional inline edit form, then Data Sync (tables) section.
// ─────────────────────────────────────────────────────────────
// Connectors whose spend ceilings AFM can control. Anything else never shows
// the card at all — Jira / Rally / Bedrock have no per-person ceiling to write.
const CEILING_CONNECTORS = {
  'github-copilot': 'github_copilot', 'claude-code': 'claude_code', cursor: 'cursor',
};
// Connectors AFM reports on but cannot cap per person, with the one-line reason.
const MONITOR_ONLY_CONNECTORS = {
  codex: 'OpenAI caps spend only per organization or project, so AFM monitors '
    + 'OpenAI API spend and writes no quota.',
};
// A one-sentence setup tip shown under the monitor-only reason.
const MONITOR_ONLY_HINTS = {
  codex: 'To keep production spend out of a person’s total, create production keys '
    + 'under an OpenAI service account; service-account spend shows as unattributed.',
};

// `{ reason, hint }` for a connector AFM only monitors (hint may be null), or
// null when the connector is not monitor-only.
export function monitorOnlyNotice(specName) {
  const reason = MONITOR_ONLY_CONNECTORS[specName];
  return reason ? { reason, hint: MONITOR_ONLY_HINTS[specName] || null } : null;
}

// One status row. `ok` is tri-state: true / false / null = NOT CHECKED.
// Null is deliberately not styled as a failure — a 200 with an empty list can
// mean the credential is perfect and the vendor feature is simply off, and that
// has a different fix from a rejected token.
function CeilingCheckRow({ label, check }) {
  const ok = check ? check.ok : null;
  const tag = ceilingStatusTag(ok);
  return (
    <Row gutter={12} style={{ padding: '6px 0' }} align="top">
      <Col flex="none" style={{ width: 150 }}><Text>{label}</Text></Col>
      <Col flex="none" style={{ width: 96 }}>{tag}</Col>
      <Col flex="auto" style={{ minWidth: 0 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>{(check && check.detail) || ''}</Text>
      </Col>
    </Row>
  );
}

/*
 * Monthly quotas — can the credential on this connection SET one?
 *
 * The user types one key, on the form above, and it reaches both halves of the
 * vendor: each speaks the same protocol with the same header for analytics and
 * for quotas (Claude's `x-api-key`, Copilot's `Bearer`). What differs is the
 * SCOPE the key was minted with, and neither vendor can be asked what scopes a
 * token holds. So this card checks rather than assumes.
 *
 * It says nothing about whether ingestion works. That question is answered
 * below, by Data Sync, with real run history behind it — a second card guessing
 * at the same thing from one probe call would be a worse answer sitting next to
 * a better one.
 *
 * The write check is behind a button, not the page load. Since scopes cannot be
 * introspected, the only way to know whether a key can set a quota is to set
 * one — the probes are built to change nothing, but they are still writes, and
 * a write that happens because a page loaded is a write nobody asked for.
 */
function useVendorCapability(provider) {
  const [state, setState] = useState({ loading: true, row: null, error: null });

  const load = useCallback(async (withWrite, probeEmail) => {
    if (!provider) {return;}
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const d = withWrite
        ? await checkVendorQuotaStatus(provider, probeEmail)
        : await getVendorQuotaStatus();
      const row = (d.providers || []).find((p) => p.provider === provider) || null;
      setState({ loading: false, row, error: null });
    } catch (e) {
      // A viewer gets 403 here by design; that is not an error worth shouting.
      setState({ loading: false, row: null, error: e });
    }
  }, [provider]);

  useEffect(() => { load(false); }, [load]);
  return { ...state, reload: load };
}

/** Where the key came from, said plainly — the first thing to know when the
 *  answer is "it cannot write". */
function CredentialSourceNote({ row }) {
  if (!row) {return null;}
  if (row.credentialSource === 'connection') {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        Using the credential from this connection
        {row.connectionName ? ` (${row.connectionName})` : ''}.
      </Text>
    );
  }
  if (row.credentialSource === 'environment') {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        Using a credential set on the server, not the one on this form. Saving a
        credential here will take over.
      </Text>
    );
  }
  return null;
}

// Which headline Tag the "Monthly quotas" card shows, given the provider
// capability row. Kept as a plain classifier (no JSX) so the render side
// is a simple lookup instead of a nested ternary.
export function spendHeadlineKind(row) {
  if (!row) {return 'none';}
  if (!row.configured) {return 'no_credential';}
  if (row.usableForWrite) {return 'can_write';}
  if (row.write && row.write.ok === false) {return 'cannot_write';}
  return NOT_CHECKED;
}

const SPEND_HEADLINE_TAGS = {
  none: null,
  no_credential: <Tag style={{ margin: 0 }}>no credential</Tag>,
  can_write: <Tag color="success" style={{ margin: 0 }}>can set quotas</Tag>,
  cannot_write: <Tag color="error" style={{ margin: 0 }}>cannot set quotas</Tag>,
  not_checked: <Tag style={{ margin: 0 }}>not checked</Tag>,
};

// Whether the "ingestion is unaffected" note should show below the
// read/write rows: only when reads work but the write check explicitly
// failed (a null/unchecked write says nothing worth calling out here).
export function shouldShowUnwritableNotice(row) {
  return !!(row && row.usableForRead && row.write && row.write.ok === false);
}

// What the permission check does to the named person, per vendor.
function ProbeWarning({ provider }) {
  if (provider === 'cursor') {
    return (
      <>
        This re-sends that person&apos;s own Cursor limit unchanged, or clears a
        limit they do not have, so nothing moves. Per-person limits need a Cursor
        Enterprise plan and an admin-scoped key.
      </>
    );
  }
  if (provider === 'github_copilot') {
    return (
      <>
        This writes a quota for that person and removes it again. If they
        already have one, it is re-written with their own number and nothing
        moves. If they do not, GitHub <strong>requires</strong> a per-person
        quota to be blocking — there is no alert-only kind — so for the moment
        it exists theirs is $1,000,000, which nothing reaches before it is
        deleted. If the removal fails, this card says so and names them.
      </>
    );
  }
  return (
    <>
      This writes a quota for that person and removes it again. If they already
      have one, it is re-written with their own number and nothing moves. If
      they do not, a $1,000,000 limit is set and then removed, so their spending
      is never actually capped. If the removal fails, this card says so and
      names them.
    </>
  );
}

function SpendCeilingCard({ provider, cap }) {
  const { loading, row, error, reload } = cap;
  const [checking, setChecking] = useState(false);
  // Pre-filled with the signed-in admin's own address. The safe path is then one
  // click, and naming somebody else is a deliberate act rather than the default.
  const [probeEmail, setProbeEmail] = useState('');
  useEffect(() => {
    if (probeEmail) {return;}
    try {
      const me = JSON.parse(localStorage.getItem('user') || '{}');
      if (me && me.email) {setProbeEmail(String(me.email).toLowerCase());}
    } catch (e) { /* no stored session — leave it empty */ }
  }, [probeEmail]);
  if (!provider || error) {return null;}

  const headline = SPEND_HEADLINE_TAGS[spendHeadlineKind(row)];

  const runCheck = async () => {
    setChecking(true);
    try {
      await reload(true, probeEmail.trim().toLowerCase());
    } finally { setChecking(false); }
  };

  return (
    <Card size="small" style={{ marginTop: 16, marginBottom: 16 }}
      title={<Space><span>Monthly quotas</span>{headline}</Space>}
      >
      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
        Whether the credential on this connection can set a monthly spending
        quota per person — the same key as above, checked rather than assumed
        because no vendor will say what scopes a key holds.
      </Paragraph>

      {/* The email, and the warning that has to sit next to it. Neither vendor
          can be asked what a token may do, so the only proof is a write — and
          the person who will receive it is named here, before the button. */}
      <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
        <Input
          size="small"
          value={probeEmail}
          onChange={(e) => setProbeEmail(e.target.value)}
          placeholder="somebody@company.com"
          allowClear
        />
        <Button size="small" loading={checking} onClick={runCheck}
          disabled={!probeEmail.trim()}>
          Check permissions
        </Button>
      </Space.Compact>
      <Paragraph type="secondary" style={{ fontSize: 11.5, marginBottom: 12 }}>
        <WarningOutlined style={{ color: '#d48806', marginRight: 5 }} />
        <ProbeWarning provider={provider} />
      </Paragraph>
      {loading && <Text type="secondary">Checking…</Text>}
      {!loading && !row && (
        <Text type="secondary">No status reported.</Text>
      )}
      {!loading && !!row && (
        <>
          <CeilingCheckRow label="Read quotas" check={row.read} />
          <CeilingCheckRow label="Set a quota" check={row.write} />
          {shouldShowUnwritableNotice(row) && (
            <Paragraph type="secondary" style={{ fontSize: 12, margin: '10px 0 0' }}>
              Ingestion is unaffected — this credential can still read usage, so
              the dashboards keep working. Only quota enforcement is off, and
              only for this vendor: each is checked on its own.
            </Paragraph>
          )}
        </>
      )}
    </Card>
  );
}

function VendorCapabilityCards({ specName }) {
  const provider = CEILING_CONNECTORS[specName];
  const cap = useVendorCapability(provider);
  const monitorOnly = provider ? null : monitorOnlyNotice(specName);
  if (monitorOnly) {
    return (
      <Card size="small" style={{ marginTop: 16, marginBottom: 16 }}
        title={<Space><span>Monthly quotas</span><Tag style={{ margin: 0 }}>monitor only</Tag></Space>}>
        <Text type="secondary" style={{ fontSize: 12 }}>{monitorOnly.reason}</Text>
        {monitorOnly.hint && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>{monitorOnly.hint}</Text>
        )}
      </Card>
    );
  }
  if (!provider) {return null;}
  // One card. Whether this credential can READ usage is already answered by the
  // Data Sync panel below, with real run history behind it — a second card
  // guessing at the same thing from one probe call would be a worse answer
  // sitting next to a better one.
  return <SpendCeilingCard provider={provider} cap={cap} />;
}

function ConnectedSourceView({
  spec, connection, latestRunDetail, extraRunDetails, recentRuns,
  schedulerSensor, schedulerDaemonHealthy, onSchedulerChanged,
  editing, syncing, onStartEdit, onCancelEdit,
  onOpenStream, onChange, onSyncStart, onDeleted,
}) {
  const [syncHistoryOpen, setSyncHistoryOpen] = useState(false);

  const onDelete = () => {
    Modal.confirm({
      title: 'Disconnect this connection?',
      content: `Removes "${connection.name}". Historical runs are retained, but no new syncs will fire and the per-source slot will be free again.`,
      okType: 'danger',
      okText: 'Disconnect',
      onOk: async () => {
        await deleteConnection(connection.id);
        message.success('Connection disconnected');
        await onDeleted();
      },
    });
  };

  return (
    <div style={{ padding: 20 }}>
      {/* Title row with Edit / Sync History / Delete buttons on the right */}
      <Row align="middle" gutter={12} style={{ marginBottom: 16 }}>
        <Col flex="auto" style={{ minWidth: 0 }}>
          <Title level={3} style={{ margin: 0 }}>{connectorTitle(spec)}</Title>
          <Text type="secondary" ellipsis>{spec.description || spec.category || ''}</Text>
        </Col>
        <Col flex="none">
          <Space style={{ marginTop: 36 }}>
            <SchedulerControl
              connector={connectorSensorKey(connection.connector_name)}
              sensor={schedulerSensor}
              daemonHealthy={schedulerDaemonHealthy}
              onChanged={onSchedulerChanged}
              size="middle"
            />
            <Button
              icon={<HistoryOutlined />}
              onClick={() => setSyncHistoryOpen(true)}
            >Sync History</Button>
            {!editing && (
              <Button icon={<EditOutlined />} onClick={onStartEdit}>Edit</Button>
            )}
            <Button
              danger
              icon={<DisconnectOutlined />}
              onClick={onDelete}
            >Disconnect</Button>
          </Space>
        </Col>
      </Row>

      <SyncHistoryDialog
        open={syncHistoryOpen}
        onClose={() => setSyncHistoryOpen(false)}
        connectionId={connection.id}
      />

      {/* Summary + (optional) extended edit form */}
      <ConnectionSummaryCard
        connection={connection}
        spec={spec}
        recentRuns={recentRuns}
        editing={editing}
      />

      {/* Only renders for the two connectors whose ceilings AFM can control,
          and only while the connection exists — a ceiling belongs to a live
          connection, not to a catalog entry. */}
      {!editing && <VendorCapabilityCards specName={spec.name} />}

      {editing && (
        <ConnectionForm
          mode="edit"
          spec={spec}
          connection={connection}
          onCancel={onCancelEdit}
          onSaved={async () => {
            onCancelEdit();
            await onChange();
          }}
        />
      )}

      {/* Data Sync section — hidden while the connection edit form is
          open so users focus on the config they're changing and avoid
          acting on the pre-edit table list.
          NOTE: keyed on connection.id so the panel remounts (and its
          local `syncingAll` / `syncingUat` / `syncingStream` state
          resets) when the user switches sources. Without the key the
          same instance is reused and an in-flight sync from the
          previous source leaves the new source's buttons stuck in
          the loading state. */}
      {!editing && (
        <DataSyncSection
          key={connection.id}
          connection={connection}
          spec={spec}
          latestRunDetail={latestRunDetail}
          extraRunDetails={extraRunDetails}
          syncing={syncing}
          onOpen={onOpenStream}
          onChange={onChange}
          onSyncStart={onSyncStart}
        />
      )}
    </div>
  );
}

// Summary card — read-only at-a-glance. Edit affordance lives in the
// title row of ConnectedSourceView (next to Sync History / Delete).
function ConnectionSummaryCard({ connection, spec, recentRuns, editing }) {
  const lastRun = recentRuns[0];
  const totalRows = recentRuns.reduce((s, r) => s + (r.rows_loaded || 0), 0);
  const lag = sourceLagSummary(spec, connection.selected_streams);

  return (
    <Card
      title={
        <Space>
          <span>Configuration</span>
          {connection.schedule
            ? <Tag color="blue" icon={<CalendarOutlined />}>{connection.schedule}</Tag>
            : <Tag>Manual only</Tag>}
        </Space>
      }
      size="small"
      style={editing ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottom: 'none' } : null}
    >
      <Row gutter={16}>
        <Col span={12}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Name">{connection.name}</Descriptions.Item>
            <Descriptions.Item label="Connector">
              {spec.display_name || spec.name}{' '}
              <Text type="secondary">v{connection.connector_version}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Load mode">
              <Tag>{connection.load_mode || 'full'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Default schedule">
              {connection.schedule || <Text type="secondary">manual</Text>}
            </Descriptions.Item>
            {lag && (
              <Descriptions.Item
                label={
                  <Space size={4}>
                    <span>Data freshness</span>
                    <Tooltip title={sourceLagExplanation(spec, lag)}>
                      <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                    </Tooltip>
                  </Space>
                }
              >
                <Space size={4} wrap>
                  <Tag icon={<ClockCircleFilled />}>{lag.label} behind source</Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    newest loadable day {lag.freshestDay} (UTC)
                  </Text>
                </Space>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Col>
        <Col span={12}>
          <Row gutter={16}>
            <Col span={12}>
              <Statistic
                title="Last run"
                value={lastRun ? timeAgo(lastRun.started_at) : '—'}
                styles={{ content: { fontSize: 18 } }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="Status"
                valueRender={() => lastRun
                  ? (
                    <Space orientation="vertical" size={2} style={{ width: '100%' }}>
                      <StatusTag status={lastRun.status} stale={lastRun.stale} tooltip={lastRun.error} />
                      {/* Live-progress phase. Drives the "Waiting in
                          queue / Syncing data from source" messaging
                          on the card. The detailed phase including
                          "Transforming data" comes from latestRunDetail
                          and is shown in the Data Sync section below. */}
                      <PhaseTag phase={lastRun.phase} />
                    </Space>
                  )
                  : <Text type="secondary">never run</Text>}
              />
            </Col>
            <Col span={12} style={{ marginTop: 12 }}>
              <Statistic
                title="Rows (last 50 runs)"
                value={totalRows}
                styles={{ content: { fontSize: 18 } }}
              />
            </Col>
            <Col span={12} style={{ marginTop: 12 }}>
              <Statistic
                title="Runs"
                value={recentRuns.length}
                styles={{ content: { fontSize: 18 } }}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Unified create/edit form. Renders 4 numbered sections.
//   mode="create" — POST /connections, no Cancel button
//   mode="edit"   — PUT /connections/{id}, expanded under summary
// ─────────────────────────────────────────────────────────────
// Initial useState values for ConnectionForm, in one place so the ternary
// chain isn't inlined across seven separate useState() initializers.
export function deriveInitialFormState({ connection, spec, allStreamNames }) {
  return {
    name: connection?.name
      || `${spec.display_name || spec.name} - ${new Date().toLocaleDateString()}`,
    selectedStreams: connection?.selected_streams || allStreamNames,
    loadMode: connection?.load_mode || INCREMENTAL,
    streamHistoryDays: connection?.stream_history_days || {},
    streamSchedules: connection?.stream_schedules || {},
    streamLoadModes: connection?.stream_load_modes || {},
    streamIngestionConfig: connection?.stream_ingestion_config || {},
  };
}

// Client-side guard rails before ConnectionForm submits its JSON-Schema
// fields. Returns a warning message to show the user, or null when the
// form is ready to proceed to form.validateFields().
export function validateConnectionFormInput({ name, selectedStreams, scheduleChoice, customCron }) {
  if (!name.trim()) {return 'Give the connection a name.';}
  if (selectedStreams.length === 0) {return 'Select at least one table to sync.';}
  if (scheduleChoice === '__custom' && !customCron.trim()) {
    return 'Enter a cron expression or pick a preset.';
  }
  return null;
}

// Request-shaping for ConnectionForm's Save/Create. `configValues` is the
// (possibly empty) object returned by form.validateFields(); edit mode
// omits `config` entirely unless the user actually re-typed something, so
// masked secret fields are never blanked out by a re-save.
export function buildConnectionPayload({
  isEdit, spec, name, selectedStreams, loadMode, cron,
  streamHistoryDays, streamSchedules, streamLoadModes, streamIngestionConfig,
  configValues,
}) {
  const shared = {
    name: name.trim(),
    selected_streams: selectedStreams,
    load_mode: loadMode,
    stream_history_days: streamHistoryDays,
    stream_schedules: streamSchedules,
    stream_load_modes: streamLoadModes,
    stream_ingestion_config: streamIngestionConfig,
  };
  if (isEdit) {
    const payload = { ...shared, schedule: cron || '' };
    if (configValues && Object.keys(configValues).length > 0) {
      payload.config = configValues;
    }
    return payload;
  }
  return {
    ...shared,
    connector_name: spec.name,
    connector_version: spec.version,
    config: configValues,
    schedule: cron || null,
  };
}

// The custom-cron field's starting text: the saved cron when editing a
// connection whose schedule is not one of the presets.
function initialCustomCron(isEdit, initialScheduleChoice, connection) {
  return isEdit && initialScheduleChoice === '__custom' ? (connection?.schedule || '') : '';
}

// Editing extends the summary card above it; creating stands alone.
function connectionFormContainerStyle(isEdit) {
  return isEdit
    ? {
        marginTop: 0,
        border: CARD_BORDER,
        borderTop: 'none',
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
        padding: 16,
        background: THEME_CONTENT_BG,
      }
    : { padding: 20 };
}

// New-connection header: "Connect <name>" and the description.
function ConnectFormHeader({ spec }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <Title level={3} style={{ margin: 0 }}>
        Connect {spec.display_name || spec.name}
      </Title>
      <Text type="secondary">{spec.description || spec.category || ''}</Text>
    </div>
  );
}

// Section 3's table list: one pick card per stream, each carrying its own
// schedule / load-mode / history / ingestion overrides.
function StreamPicker({
  fullSpec, selectedStreams, setSelectedStreams, cron, loadMode,
  streamSchedules, setStreamSchedules, streamLoadModes, setStreamLoadModes,
  streamHistoryDays, setStreamHistoryDays, streamIngestionConfig, setStreamOverride,
}) {
  if ((fullSpec.streams || []).length === 0) {
    return <Empty description="This connector exposes no streams." />;
  }
  return (
    <Checkbox.Group
      value={selectedStreams}
      onChange={setSelectedStreams}
      style={{ width: '100%' }}
    >
      <Row gutter={[8, 8]}>
        {(fullSpec.streams || []).map((s) => (
          <Col span={12} key={s.name}>
            <StreamPickCard
              stream={s}
              isSelected={selectedStreams.includes(s.name)}
              connectionSchedule={cron}
              connectionLoadMode={loadMode}
              scheduleOverride={streamSchedules[s.name] ?? null}
              onScheduleOverrideChange={(next) => setStreamSchedules((prev) => {
                const out = { ...prev };
                if (next == null || next === '') {delete out[s.name];}
                else {out[s.name] = next;}
                return out;
              })}
              loadModeOverride={streamLoadModes[s.name] ?? null}
              onLoadModeOverrideChange={(next) => setStreamLoadModes((prev) => {
                const out = { ...prev };
                if (!next) {delete out[s.name];}
                else {out[s.name] = next;}
                return out;
              })}
              historyDays={streamHistoryDays[s.name] ?? null}
              onHistoryDaysChange={(next) => setStreamHistoryDays((prev) => {
                const out = { ...prev };
                if (next == null || next <= 0) {delete out[s.name];}
                else {out[s.name] = next;}
                return out;
              })}
              override={streamIngestionConfig[s.name] || null}
              onOverrideChange={(next) => setStreamOverride(s.name, next)}
            />
          </Col>
        ))}
      </Row>
    </Checkbox.Group>
  );
}

function FormActions({ submitting, handleSubmit, isEdit, onCancel }) {
  return (
    <Space>
      <Button
        type="primary"
        size="large"
        loading={submitting}
        icon={<CheckCircleFilled />}
        onClick={handleSubmit}
      >{isEdit ? 'Save changes' : 'Create connection'}</Button>
      {onCancel && (
        <Button size="large" onClick={onCancel}>Cancel</Button>
      )}
    </Space>
  );
}

function ConnectionForm({ mode, spec, connection = null, onCancel, onSaved }) {
  const isEdit = mode === 'edit';

  // Need full spec (config_schema). Catalog returns it but we re-fetch
  // when missing (e.g. lightweight list view).
  const [fullSpec, setFullSpec] = useState(spec);
  const [loadingSpec, setLoadingSpec] = useState(false);
  useEffect(() => {
    if (spec.config_schema) { setFullSpec(spec); return; }
    setLoadingSpec(true);
    getConnectorSpec(spec.name, spec.version)
      .then(setFullSpec)
      .catch(() => {})
      .finally(() => setLoadingSpec(false));
  }, [spec]);

  // In edit mode the list endpoint omits `config` (no credentials) — so
  // fetch the detail to prefill the form. Without this, switching to edit
  // shows a blank Authentication section even though the connection has
  // values. Fixes the "edit doesn't fill the form" bug.
  const [initialConfig, setInitialConfig] = useState(connection?.config || null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  useEffect(() => {
    if (!isEdit || !connection?.id) { setInitialConfig(null); return; }
    if (connection.config) { setInitialConfig(connection.config); return; }
    setLoadingDetail(true);
    getConnection(connection.id)
      .then((d) => setInitialConfig(d.config || {}))
      .catch(() => setInitialConfig({}))
      .finally(() => setLoadingDetail(false));
  }, [isEdit, connection]);

  const allStreamNames = useMemo(
    () => (fullSpec.streams || []).map((s) => s.name),
    [fullSpec],
  );

  const initialFormState = deriveInitialFormState({ connection, spec, allStreamNames });
  const [form] = Form.useForm();
  const [name, setName] = useState(initialFormState.name);
  const [selectedStreams, setSelectedStreams] = useState(initialFormState.selectedStreams);
  useEffect(() => {
    if (!connection) {setSelectedStreams(allStreamNames);}
  }, [allStreamNames, connection]);

  const [loadMode, setLoadMode] = useState(initialFormState.loadMode);

  // Per-stream history window (initial backfill cap). Cursor-fielded streams
  // can opt in to "Last N days" instead of pulling everything on first run.
  const [streamHistoryDays, setStreamHistoryDays] = useState(initialFormState.streamHistoryDays);

  // Per-stream cron schedule overrides. Streams without an entry inherit
  // the connection-level `schedule`. Map: { stream_name: cron string }.
  const [streamSchedules, setStreamSchedules] = useState(initialFormState.streamSchedules);

  // Per-stream load-mode overrides. Streams without an entry inherit the
  // connection-level `load_mode`. Map: { stream_name: 'full' | 'incremental' }.
  const [streamLoadModes, setStreamLoadModes] = useState(initialFormState.streamLoadModes);

  // Per-stream ingestion-params overrides (page_size / extract_params /
  // rate_limit). Edited inline on each stream card in section 3; the
  // orchestrator applies them right before `connector.extract()`.
  // Streams without an entry inherit the connector-declared defaults.
  const [streamIngestionConfig, setStreamIngestionConfig] = useState(
    initialFormState.streamIngestionConfig,
  );
  const setStreamOverride = useCallback((streamName, override) => {
    setStreamIngestionConfig((prev) => {
      const next = { ...prev };
      if (!override || Object.keys(override).length === 0) {
        delete next[streamName];
      } else {
        next[streamName] = override;
      }
      return next;
    });
  }, []);

  // Derive schedule choice from connection.schedule when editing.
  const initialScheduleChoice = useMemo(() => {
    if (!isEdit) {return '0 2 * * *';}
    const s = connection?.schedule || '';
    const known = CONNECTION_SCHEDULE_PRESETS.some((p) => p.value === s);
    if (known) {return s;}
    return s ? '__custom' : '';
  }, [isEdit, connection]);
  const [scheduleChoice, setScheduleChoice] = useState(initialScheduleChoice);
  const [customCron, setCustomCron] = useState(
    initialCustomCron(isEdit, initialScheduleChoice, connection),
  );

  // Sync the AntD Form's internal state when initialConfig arrives async.
  useEffect(() => {
    if (initialConfig && Object.keys(initialConfig).length > 0) {
      form.setFieldsValue(initialConfig);
    }
  }, [initialConfig, form]);

  const [submitting, setSubmitting] = useState(false);
  const cron = scheduleChoice === '__custom' ? customCron.trim() : scheduleChoice;

  const handleSubmit = async () => {
    const warning = validateConnectionFormInput({ name, selectedStreams, scheduleChoice, customCron });
    if (warning) {
      message.warning(warning);
      return;
    }
    let configValues;
    try {
      configValues = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildConnectionPayload({
        isEdit, spec, name, selectedStreams, loadMode, cron,
        streamHistoryDays, streamSchedules, streamLoadModes, streamIngestionConfig,
        configValues,
      });
      if (isEdit) {
        await updateConnection(connection.id, payload);
        message.success('Connection updated');
      } else {
        await createConnection(payload);
        message.success('Connection created');
      }
      onSaved();
    } catch (err) {
      message.error(`${isEdit ? 'Update' : 'Create'} failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingSpec || loadingDetail) {
    return <div style={{ padding: 64, textAlign: 'center' }}><Spin /></div>;
  }

  // Wrap in a container that "extends" the summary card visually when editing.
  const containerStyle = connectionFormContainerStyle(isEdit);

  return (
    <div style={containerStyle}>
      {!isEdit && <ConnectFormHeader spec={spec} />}

      <FormSection number="1" title="Name this source"
        hint="A short label so you can tell it apart in lists and logs.">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={spec.display_name || spec.name}
          size="large"
        />
      </FormSection>

      <FormSection number="2" title="Authentication & configuration"
        hint={isEdit
          ? 'Re-enter credentials to rotate them, or leave blank to keep existing values.'
          : 'Credentials are stored encrypted; this form mirrors the connector\'s JSON-Schema.'}>
        <ConnectorSetupGuide spec={fullSpec} defaultOpen={!isEdit} />
        <Form form={form} layout="vertical">
          <JsonSchemaForm
            schema={fullSpec.config_schema}
            form={form}
            initialValues={initialConfig || undefined}
          />
        </Form>
      </FormSection>

      <FormSection number="3" title="Tables to sync"
        hint="Pick the tables to pull and how to load them.">
        <div style={{ marginBottom: 12 }}>
          <Radio.Group
            value={loadMode}
            onChange={(e) => setLoadMode(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="incremental">Incremental — append new rows</Radio.Button>
            <Radio.Button value="full">Full refresh — replace each run</Radio.Button>
          </Radio.Group>
        </div>
        <StreamPicker
          fullSpec={fullSpec} selectedStreams={selectedStreams} setSelectedStreams={setSelectedStreams}
          cron={cron} loadMode={loadMode}
          streamSchedules={streamSchedules} setStreamSchedules={setStreamSchedules}
          streamLoadModes={streamLoadModes} setStreamLoadModes={setStreamLoadModes}
          streamHistoryDays={streamHistoryDays} setStreamHistoryDays={setStreamHistoryDays}
          streamIngestionConfig={streamIngestionConfig} setStreamOverride={setStreamOverride}
        />
      </FormSection>

      <FormSection number="4" title="Sync schedule"
        hint="When to run this connection. Per-table overrides live in the Data Sync section.">
        {/* The source's publication lag belongs HERE, next to the cron
            picker, because this is where the wrong mental model forms:
            people reach for a tighter schedule to get fresher numbers,
            and against a source that publishes a day behind, every cron
            in the list produces exactly the same newest day. Narrowed to
            the tables selected in section 3, so it tracks what this
            connection will actually pull. */}
        {(() => {
          const lag = sourceLagSummary(fullSpec, selectedStreams);
          if (!lag) {return null;}
          return (
            <Alert
              type="info"
              showIcon
              icon={<ClockCircleFilled />}
              style={{ marginBottom: 12 }}
              title={
                <Text>
                  This source publishes with a{' '}
                  <Text strong>{lag.label}</Text> delay — the newest day any
                  schedule can load right now is{' '}
                  <Text strong>{lag.freshestDay}</Text> (UTC).
                </Text>
              }
              description={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  A more frequent schedule picks up late-arriving and revised
                  figures sooner, but cannot make the newest day fresher.
                  {lag.max === 1
                    ? ' Month-to-date totals read zero on the 1st of each month'
                      + ' until that day is published.'
                    : ` Month-to-date totals read zero for the first ${lag.label}`
                      + ' of each month until the source publishes them.'}
                  {lag.mixed && ' Some selected tables publish sooner than others.'}
                </Text>
              }
            />
          );
        })()}
        <Radio.Group
          value={scheduleChoice}
          onChange={(e) => setScheduleChoice(e.target.value)}
          style={{ width: '100%' }}
        >
          <Space orientation="vertical" size={4} style={{ width: '100%' }}>
            {CONNECTION_SCHEDULE_PRESETS.map((p) => (
              <Radio key={p.value || 'manual'} value={p.value}>
                <Space>
                  <Text strong>{p.label}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>{p.hint}</Text>
                  {p.value && p.value !== '__custom' && <code style={{ fontSize: 11 }}>{p.value}</code>}
                </Space>
              </Radio>
            ))}
          </Space>
        </Radio.Group>
        {scheduleChoice === '__custom' && (
          <Input
            placeholder="*/15 * * * *"
            value={customCron}
            onChange={(e) => setCustomCron(e.target.value)}
            style={{ marginTop: 8, maxWidth: 280 }}
          />
        )}
      </FormSection>

      <Divider />

      <FormActions submitting={submitting} handleSubmit={handleSubmit} isEdit={isEdit} onCancel={onCancel} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// One card in Section 3 "Tables to sync": checkbox + PK/cursor
// summary + (when selected) collapsible per-stream config editor.
// Edits five overrides — schedule, load mode, backfill history,
// page size / rate limit / request params — all of which the
// parent form persists into its respective state slot. The
// orchestrator merges them in just before `connector.extract()`.
// ─────────────────────────────────────────────────────────────
// Whether a stream's page_size / rate_limit / extract_params override
// carries any real value (vs. an empty/cleared override object).
function hasExtractParams(override) {
  return !!override.extract_params && Object.keys(override.extract_params).length > 0;
}

export function hasIngestionOverride(override) {
  if (!override) {return false;}
  return !!(
    override.page_size != null
    || (override.rate_limit || '').trim()
    || hasExtractParams(override)
  );
}

// Whether a stream's schedule / load-mode / backfill-window override
// differs from "inherit the connection-level default".
export function hasSyncOverride({ scheduleOverride, loadModeOverride, historyDays }) {
  const hasSchedule = !!(scheduleOverride && String(scheduleOverride).trim());
  const hasLoadMode = !!(loadModeOverride && loadModeOverride !== INHERIT);
  const hasHistory = historyDays != null && historyDays > 0;
  return hasSchedule || hasLoadMode || hasHistory;
}

function StreamPickCard({
  stream, isSelected,
  connectionSchedule, connectionLoadMode,
  scheduleOverride, onScheduleOverrideChange,
  loadModeOverride, onLoadModeOverrideChange,
  historyDays, onHistoryDaysChange,
  override, onOverrideChange,
}) {
  const hasAnyOverride = hasIngestionOverride(override)
    || hasSyncOverride({ scheduleOverride, loadModeOverride, historyDays });
  // Auto-expand when any override already exists so users see what's
  // there before tweaking; collapsed by default for clean streams.
  const [expanded, setExpanded] = useState(hasAnyOverride);

  return (
    <Card size="small" styles={{ body: { padding: 10 } }}>
      <Checkbox value={stream.name}>
        <Text strong>{stream.name}</Text>
      </Checkbox>
      {hasAnyOverride && (
        <Tag color="gold" style={{ marginLeft: 6, fontSize: 10 }}>overridden</Tag>
      )}
      <div style={{ paddingLeft: 24, fontSize: 11 }}>
        <Text type="secondary">
          {(stream.primary_key || []).length > 0 && <>PK: <code>{stream.primary_key.join(', ')}</code></>}
          {stream.cursor_field && <> · cursor: <code>{stream.cursor_field}</code></>}
        </Text>
      </div>
      {isSelected && (
        <div style={{ paddingLeft: 24, marginTop: 4 }}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, fontSize: 11, height: 18 }}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? '▾ Hide' : '▸ Configure'} table settings
          </Button>
          {expanded && (
            <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed var(--theme-border, #f0f0f0)' }}>
              <StreamSyncFields
                stream={stream}
                connectionSchedule={connectionSchedule}
                connectionLoadMode={connectionLoadMode}
                scheduleOverride={scheduleOverride}
                onScheduleOverrideChange={onScheduleOverrideChange}
                loadModeOverride={loadModeOverride}
                onLoadModeOverrideChange={onLoadModeOverrideChange}
                historyDays={historyDays}
                onHistoryDaysChange={onHistoryDaysChange}
              />
              <StreamIngestionFields
                stream={stream}
                override={override || {}}
                onChange={onOverrideChange}
              />
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// Schedule / load mode / backfill window editor. Each field is
// optional and falls back to the connection-level default when unset.
// StreamSyncFields' schedule <Select> value: `''`/null means "inherit the
// connection-level cron"; a value not among the known presets is a
// custom cron the user typed in.
export function resolveScheduleSelectValue(scheduleOverride) {
  if (scheduleOverride == null || scheduleOverride === '') {return INHERIT;}
  const isKnownPreset = CONNECTION_SCHEDULE_PRESETS.some((p) => p.value === scheduleOverride);
  return isKnownPreset ? scheduleOverride : '__custom';
}

function StreamSyncFields({
  stream,
  connectionSchedule, connectionLoadMode,
  scheduleOverride, onScheduleOverrideChange,
  loadModeOverride, onLoadModeOverrideChange,
  historyDays, onHistoryDaysChange,
}) {
  // Schedule: a select with the same preset shape as the connection-level
  // form. `''` = inherit connection-level cron; '__custom' surfaces a free
  // text input.
  const scheduleSelect = resolveScheduleSelectValue(scheduleOverride);

  const onScheduleSelectChange = (v) => {
    if (v === INHERIT) {onScheduleOverrideChange(null);}
    else if (v === '__custom') {onScheduleOverrideChange(scheduleOverride || '*/15 * * * *');}
    else {onScheduleOverrideChange(v);}
  };

  const supportsIncremental = !!stream.cursor_field;

  return (
    <Space orientation="vertical" size={6} style={{ width: '100%', marginBottom: 6 }}>
      <div>
        <Text type="secondary" style={{ fontSize: 11, marginRight: 6 }}>Schedule:</Text>
        <Select
          size="small"
          value={scheduleSelect}
          onChange={onScheduleSelectChange}
          style={{ width: 200 }}
          options={[
            { value: INHERIT, label: `Inherit (${connectionSchedule || 'manual'})` },
            ...CONNECTION_SCHEDULE_PRESETS.filter((p) => p.value !== '__custom').map((p) => ({
              value: p.value, label: p.label,
            })),
            { value: '__custom', label: CUSTOM_CRON_LABEL },
          ]}
        />
        {scheduleSelect === '__custom' && (
          <Input
            size="small"
            value={scheduleOverride || ''}
            placeholder="*/15 * * * *"
            onChange={(e) => onScheduleOverrideChange(e.target.value)}
            style={{ width: 160, marginLeft: 6 }}
          />
        )}
      </div>
      <div>
        <Text type="secondary" style={{ fontSize: 11, marginRight: 6 }}>Load mode:</Text>
        <Select
          size="small"
          value={loadModeOverride || INHERIT}
          onChange={(v) => onLoadModeOverrideChange(v === INHERIT ? null : v)}
          style={{ width: 200 }}
          options={[
            { value: INHERIT, label: `Inherit (${connectionLoadMode || 'full'})` },
            { value: INCREMENTAL, label: 'Delta (incremental)', disabled: !supportsIncremental },
            { value: 'full',        label: 'Full refresh' },
          ]}
        />
        {!supportsIncremental && (
          <Text type="secondary" style={{ fontSize: 11, marginLeft: 6, fontStyle: 'italic' }}>
            no cursor → delta n/a
          </Text>
        )}
      </div>
      {supportsIncremental && (
        <div>
          <Text type="secondary" style={{ fontSize: 11, marginRight: 6 }}>Backfill window:</Text>
          <Space.Compact size="small" style={{ width: 110, verticalAlign: 'middle' }}>
            <InputNumber
              min={1}
              max={3650}
              step={1}
              value={historyDays ?? null}
              placeholder="all time"
              onChange={(v) => onHistoryDaysChange(v == null ? null : Number(v))}
              style={{ flex: 1, minWidth: 0 }}
            />
            <Space.Addon>days</Space.Addon>
          </Space.Compact>
          <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
            cap first sync; empty = no cap
          </Text>
        </div>
      )}
    </Space>
  );
}

// ─────────────────────────────────────────────────────────────
// Three-field editor for per-stream ingestion params. All fields
// are optional — empty values fall back to the connector-declared
// defaults on the Stream dataclass.
// ─────────────────────────────────────────────────────────────
// Strip empties from a pending ingestion-override patch so the
// "overridden" badge only lights up when something is actually set, and
// the backend's own cleaning helper has nothing left to do. Returns null
// when nothing survives (i.e. the override should be cleared entirely).
export function cleanIngestionOverride(next) {
  const out = {};
  if (next.page_size != null && next.page_size > 0) {out.page_size = next.page_size;}
  const rl = (next.rate_limit || '').trim();
  if (rl) {out.rate_limit = rl;}
  if (next.extract_params && Object.keys(next.extract_params).length > 0) {
    out.extract_params = next.extract_params;
  }
  return Object.keys(out).length > 0 ? out : null;
}

// Merge a base extract_params object with the user's ordered custom-row
// edits, skipping any row whose key collides with a connector-declared
// param (those are edited through their own dedicated field, not here).
export function applyCustomParamRows(base, rows, declaredKeys) {
  const ep = { ...base };
  for (const { key, value } of rows) {
    const k = (key || '').trim();
    const v = (value == null ? '' : String(value)).trim();
    if (k && v && !declaredKeys.includes(k)) {ep[k] = v;}
  }
  return ep;
}

// Derive the value rows for a repeated query param (key ending in `[]`,
// e.g. Anthropic's `group_by[]`): one row per comma-joined value,
// preferring the override over the connector's literal default, padded
// out to the larger of the two counts (minimum 1 row).
export function resolveArrayParamRows({ literalDefault, overrideValue, isOverridden }) {
  const defaultVals = (literalDefault || '').split(',').map((v) => v.trim()).filter(Boolean);
  const overrideVals = isOverridden
    ? String(overrideValue).split(',').map((v) => v.trim())
    : [];
  const rowCount = Math.max(1, defaultVals.length, overrideVals.length);
  const effectiveVals = isOverridden ? [...overrideVals] : [...defaultVals];
  while (effectiveVals.length < rowCount) {effectiveVals.push('');}
  return effectiveVals;
}

// Derive the displayed value/placeholder/muted-style flag for a single
// (non-array) declared param field.
export function resolveScalarParamField({ overrideValue, isOverridden, literalDefault, desc }) {
  const value = isOverridden ? overrideValue : (literalDefault ?? '');
  const showingDefault = !isOverridden && literalDefault != null;
  let placeholder;
  if (literalDefault != null) {
    placeholder = '';
  } else if (desc) {
    placeholder = `default: ${desc}`;
  } else {
    placeholder = 'override value';
  }
  return { value, showingDefault, placeholder };
}

function PageSizeField({ override, stream, pushOverride }) {
  return (
    <div>
      <Text type="secondary" style={{ fontSize: 11, marginRight: 6 }}>Page size:</Text>
      <InputNumber
        size="small"
        min={1}
        max={100000}
        step={1}
        value={override.page_size ?? null}
        placeholder={stream.page_size != null ? String(stream.page_size) : 'default'}
        onChange={(v) => pushOverride({ page_size: v == null ? null : Number(v) })}
        style={{ width: 110 }}
      />
      {stream.page_size != null && (
        <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
          connector default: {stream.page_size}
        </Text>
      )}
    </div>
  );
}

function RateLimitField({ override, stream, pushOverride }) {
  return (
    <div>
      <Text type="secondary" style={{ fontSize: 11, marginRight: 6 }}>Rate limit:</Text>
      <Input
        size="small"
        value={override.rate_limit || ''}
        placeholder={stream.rate_limit || 'e.g. 1000 req/hr'}
        onChange={(e) => pushOverride({ rate_limit: e.target.value })}
        style={{ width: 220 }}
      />
    </div>
  );
}

function StreamIngestionFields({ stream, override, onChange }) {
  const declaredParams = stream.extract_params || {};
  const declaredParamDefaults = stream.extract_param_defaults || {};
  // extract_params is edited as an ordered list of {key, value} pairs
  // so duplicate-key edits don't collapse mid-typing; serialised back
  // to an object before being pushed up.
  const declaredKeys = useMemo(() => Object.keys(declaredParams), [declaredParams]);
  const overrideParams = override.extract_params || {};

  // Custom params = override entries whose key is NOT in the connector's
  // declared list. We keep them as an ordered list so the user can edit
  // a key mid-typing without the row collapsing.
  const initialCustomRows = useMemo(
    () => Object.entries(overrideParams)
      .filter(([k]) => !declaredKeys.includes(k))
      .map(([key, value]) => ({ key, value: String(value) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [customRows, setCustomRows] = useState(initialCustomRows);

  // Repeated query params (keys ending in `[]`) like Anthropic's
  // `group_by[]=product&group_by[]=model` render as one row per value.
  // The override is stored as a single comma-joined string so the
  // existing extract_params: dict[str, str] storage still works; the
  // connector re-splits the value on the way out (see
  // connectors/claude-code/1.0.0/connector.py:_parse_group_by).

  const pushOverride = (patch) => {
    onChange(cleanIngestionOverride({ ...override, ...patch }));
  };

  const setDeclaredParam = (key, value) => {
    const trimmed = (value == null ? '' : String(value)).trim();
    const ep = { ...overrideParams };
    if (trimmed) {
      ep[key] = trimmed;
    } else {
      delete ep[key];
    }
    // Re-merge custom rows so a declared-param edit doesn't drop them.
    pushOverride({ extract_params: applyCustomParamRows(ep, customRows, declaredKeys) });
  };

  const commitCustomRows = (rows) => {
    setCustomRows(rows);
    // Start with current declared-param overrides so editing custom rows
    // doesn't wipe them.
    const declaredBase = {};
    for (const k of declaredKeys) {
      if (overrideParams[k] != null) {declaredBase[k] = overrideParams[k];}
    }
    pushOverride({ extract_params: applyCustomParamRows(declaredBase, rows, declaredKeys) });
  };

  return (
    <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed var(--theme-border, #f0f0f0)' }}>
      <Space orientation="vertical" size={6} style={{ width: '100%' }}>
        <PageSizeField override={override} stream={stream} pushOverride={pushOverride} />
        <RateLimitField override={override} stream={stream} pushOverride={pushOverride} />
        <div>
          <Text type="secondary" style={{ fontSize: 11 }}>Request params:</Text>
          <div style={{ marginTop: 2 }}>
            {/* Declared params — pre-populated rows so users can see and
                override what the connector already sends. Empty input
                falls back to the connector's hardcoded default. */}
            {declaredKeys.length === 0 && customRows.length === 0 && (
              <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>
                no params declared by the connector
              </Text>
            )}
            {declaredKeys.flatMap((key) => {
              const desc = declaredParams[key];
              const literalDefault = declaredParamDefaults[key];
              const isOverridden = overrideParams[key] != null;
              const isArrayParam = key.endsWith('[]');

              if (isArrayParam) {
                // Pre-fill the input with the connector's literal default when
                // there's no override, so users can see exactly what's sent
                // to the API. Editing any row materialises the override.
                const effectiveVals = resolveArrayParamRows({
                  literalDefault, overrideValue: overrideParams[key], isOverridden,
                });

                const commitArrayRow = (idx, rowText) => {
                  const next = [...effectiveVals];
                  next[idx] = rowText;
                  const joined = next.map((v) => v.trim()).filter(Boolean).join(',');
                  setDeclaredParam(key, joined);
                };

                return effectiveVals.map((_, i) => {
                  const rowValue = effectiveVals[i] || '';
                  const rowShowingDefault = !isOverridden && rowValue !== '';
                  return (
                    <div
                      key={`${key}-${i}`}
                      style={{ display: 'flex', gap: 4, marginTop: 4, alignItems: 'center' }}
                    >
                      <Tooltip title={desc}>
                        <Tag
                          color={isOverridden ? 'gold' : 'blue'}
                          style={{ minWidth: 110, fontSize: 11, margin: 0, cursor: 'help' }}
                        >
                          {key}
                        </Tag>
                      </Tooltip>
                      <Input
                        size="small"
                        value={rowValue}
                        placeholder="value"
                        onChange={(e) => commitArrayRow(i, e.target.value)}
                        style={{
                          width: 200,
                          color: rowShowingDefault ? '#8c8c8c' : undefined,
                          fontStyle: rowShowingDefault ? 'italic' : undefined,
                        }}
                      />
                    </div>
                  );
                });
              }

              // Non-array param: pre-fill input with the literal default if
              // declared, in muted style; user typing materialises the
              // override. For params without a literal default (e.g.
              // starting_at, runtime-computed) keep the description in the
              // placeholder so the user still has a hint.
              const { value, showingDefault, placeholder } = resolveScalarParamField({
                overrideValue: overrideParams[key], isOverridden, literalDefault, desc,
              });
              return [(
                <div key={key} style={{ display: 'flex', gap: 4, marginTop: 4, alignItems: 'center' }}>
                  <Tooltip title={desc}>
                    <Tag
                      color={isOverridden ? 'gold' : 'blue'}
                      style={{ minWidth: 110, fontSize: 11, margin: 0, cursor: 'help' }}
                    >
                      {key}
                    </Tag>
                  </Tooltip>
                  <Input
                    size="small"
                    value={value}
                    placeholder={placeholder}
                    onChange={(e) => setDeclaredParam(key, e.target.value)}
                    style={{
                      width: 200,
                      color: showingDefault ? '#8c8c8c' : undefined,
                      fontStyle: showingDefault ? 'italic' : undefined,
                    }}
                  />
                </div>
              )];
            })}

            {/* Custom params — for keys the connector didn't declare. */}
            {customRows.map((row, i) => (
              <div key={`custom-${i}`} style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                <Input
                  size="small"
                  value={row.key}
                  placeholder="custom param"
                  onChange={(e) => commitCustomRows(
                    customRows.map((r, idx) => (idx === i ? { ...r, key: e.target.value } : r)),
                  )}
                  style={{ width: 110 }}
                />
                <Input
                  size="small"
                  value={row.value}
                  placeholder="value"
                  onChange={(e) => commitCustomRows(
                    customRows.map((r, idx) => (idx === i ? { ...r, value: e.target.value } : r)),
                  )}
                  style={{ width: 150 }}
                />
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => commitCustomRows(customRows.filter((_, idx) => idx !== i))}
                />
              </div>
            ))}
            <Button
              size="small"
              type="dashed"
              style={{ marginTop: 4, fontSize: 11, height: 22 }}
              icon={<PlusOutlined />}
              onClick={() => setCustomRows((rs) => [...rs, { key: '', value: '' }])}
            >
              Add custom param
            </Button>
          </div>
        </div>
      </Space>
    </div>
  );
}

function FormSection({ number, title, hint, children }) {
  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Space align="start" size={12} style={{ marginBottom: 12 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 14,
          background: '#1677ff', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 600, flexShrink: 0,
        }}>{number}</div>
        <div>
          <Text strong style={{ fontSize: 15 }}>{title}</Text>
          {hint && <div><Text type="secondary" style={{ fontSize: 12 }}>{hint}</Text></div>}
        </div>
      </Space>
      <div style={{ paddingLeft: 40 }}>{children}</div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Data Sync section: per-stream table + global "Sync now"
// ─────────────────────────────────────────────────────────────
function DataSyncSection({ connection, spec, latestRunDetail, extraRunDetails, syncing, onOpen, onChange, onSyncStart }) {
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingUat, setSyncingUat] = useState(false);
  const [uatSampleLimit, setUatSampleLimit] = useState(UAT_SAMPLE_LIMIT_DEFAULT);
  const [syncingStream, setSyncingStream] = useState(null);
  const [refreshingHealth, setRefreshingHealth] = useState(false);
  const uatMode = isUatMode();

  const refreshHealth = async () => {
    setRefreshingHealth(true);
    try {
      await testConnection(connection.id);
      message.success('Test Connection refreshed');
      await onChange();
    } catch (e) {
      message.error(`Test Connection failed: ${e.message}`);
    } finally {
      setRefreshingHealth(false);
    }
  };


  const syncAll = async () => {
    setSyncingAll(true);
    if (onSyncStart) {onSyncStart(connection.id);}
    try {
      await triggerSync(connection.id);
      message.success('Sync triggered for all selected tables');
      await onChange();
    } catch (e) {
      message.error(`Failed: ${e.message}`);
    } finally {
      setSyncingAll(false);
    }
  };

  // UAT-only — replays cached Test Connection samples through the same
  // Dagster job, landing rows in the bronze tables without hitting the
  // live source. The API first re-runs Test Connection with
  // `sample_limit=uatSampleLimit` so QA can pick how many rows make it
  // through bronze → dbt → silver. Hidden in production builds.
  const syncAllUat = async () => {
    setSyncingUat(true);
    if (onSyncStart) {onSyncStart(connection.id);}
    try {
      await triggerSync(connection.id, null, {
        useSaved: true,
        sampleLimit: uatSampleLimit,
      });
      message.success(
        `UAT sync triggered (refreshing samples to ${uatSampleLimit} row${uatSampleLimit === 1 ? '' : 's'} per stream, then replaying)`,
      );
      await onChange();
    } catch (e) {
      message.error(`Failed: ${e.message}`);
    } finally {
      setSyncingUat(false);
    }
  };

  const syncOne = async (streamNm) => {
    setSyncingStream(streamNm);
    if (onSyncStart) {onSyncStart(connection.id);}
    try {
      await triggerSync(connection.id, streamNm);
      message.success(`Sync triggered for "${streamNm}"`);
      await onChange();
    } catch (e) {
      message.error(`Failed: ${e.message}`);
    } finally {
      setSyncingStream(null);
    }
  };

  return (
    <Card
      style={{ marginTop: 24 }}
      title={
        <Space>
          <span>Data Sync</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {(connection.selected_streams || (spec.streams || []).map((s) => s.name)).length} tables
          </Text>
        </Space>
      }
      extra={
        <Space>
          <Button
            icon={<ExperimentOutlined />}
            onClick={refreshHealth}
            loading={refreshingHealth}
          >Test connection</Button>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={syncingAll}
            onClick={syncAll}
          >Sync now</Button>
          {uatMode && (
            <Space.Compact>
              <Tooltip title="Number of rows to retrieve per stream. The API re-runs Test Connection with this cap, then replays the cached samples through bronze → dbt → silver.">
                <Select
                  value={uatSampleLimit}
                  onChange={setUatSampleLimit}
                  disabled={syncingUat}
                  style={{ width: 88 }}
                  options={[5, 10, 25, 50, 100, 250, 500, 1000].map((n) => ({
                    value: n,
                    label: `${n} rows`,
                  }))}
                />
              </Tooltip>
              <Tooltip title="UAT only — refresh cached Test Connection samples to the selected row count, then push them through the full sync pipeline (bronze → dbt → silver).">
                <Button
                  icon={<ThunderboltOutlined />}
                  onClick={syncAllUat}
                  loading={syncingUat}
                >Sync now &mdash; UAT</Button>
              </Tooltip>
            </Space.Compact>
          )}
        </Space>
      }
      size="small"
    >
      {/* "Sending ingestion request…" banner — shown from the moment
          a sync button is clicked until a Dagster run becomes visible
          (with a 1.2s minimum so users actually see it). Sits above
          the phase banner so the right pane mirrors what the source
          card on the left is showing. */}
      {syncing && (
        <Alert
          type="info"
          showIcon
          icon={<SyncOutlined spin />}
          title={<strong>Sending ingestion request…</strong>}
          style={{ marginBottom: 12 }}
        />
      )}
      {/* Phase banner — surfaces "Waiting in queue / Syncing data from
          source / Transforming data" at a glance so the page never
          looks idle while a run is in flight. Shown only when there's
          an active or recently-completed run; otherwise the streams
          table tells the whole story. */}
      <PhaseBanner detail={latestRunDetail} />
      <StreamsTable
        connection={connection}
        spec={spec}
        latestRunDetail={latestRunDetail}
        extraRunDetails={extraRunDetails}
        syncingStream={syncingStream}
        refreshingHealth={refreshingHealth}
        onOpen={onOpen}
        onChange={onChange}
        onSyncOne={syncOne}
      />
    </Card>
  );
}

// Phase banner: a one-line "this is what the pipeline is currently doing"
// indicator for the active run. Sits above the per-stream table so the
// user knows at a glance whether we're queued / pulling from the source /
// transforming, without having to read the per-row column.
// The banner is noise once a run has reached a terminal, unremarkable
// state — the per-row badges already tell that story.
export function shouldShowPhaseBanner(detail) {
  if (!detail || !detail.phase) {return false;}
  return detail.phase !== PHASE.SUCCESS && detail.phase !== PHASE.UNKNOWN;
}

// Alert color for a given run phase.
export function phaseBannerAlertType(phase) {
  if (phase === PHASE.FAILED || phase === PHASE.STALE) {return 'error';}
  if (phase === PHASE.PARTIAL) {return 'warning';}
  return 'info';
}

function PhaseBanner({ detail }) {
  if (!shouldShowPhaseBanner(detail)) {return null;}
  const phase = detail.phase;
  const meta = _PHASE_META[phase] || _PHASE_META[PHASE.UNKNOWN];
  const alertType = phaseBannerAlertType(phase);
  const label = phaseLabel(phase);
  const detailSuffix = detail.phase_detail ? ` — ${detail.phase_detail}` : '';
  return (
    <Alert
      type={alertType}
      showIcon
      icon={meta.icon}
      title={
        <Space size={6}>
          <strong>{label}{detailSuffix}</strong>
          {detail.bronze_run_id && (phase === PHASE.TRANSFORMING || phase === PHASE.EXTRACTED) && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              transform run: <code>{detail.bronze_run_id.slice(0, 8)}</code>
            </Text>
          )}
        </Space>
      }
      style={{ marginBottom: 12 }}
    />
  );
}

// ─────────────────────────────────────────────────────────────
// Streams table with per-row Sync now
// ─────────────────────────────────────────────────────────────
// Health/expected-rows classification for the StreamsTable "Expected"
// column. Keeps all the branching in one pure place so the render
// callback becomes a plain dispatch on `kind`.
// The detail lines for the "ready, with totals" state: a total-rows
// line (if any), then either the history-window line or the
// request-count line — mutually exclusive, so at most 2 lines.
function expectedCellLines({ records, requests, windowed, histDays, willApplyHistory }) {
  const lines = [];
  if (records != null) {lines.push({ type: 'total', records });}
  if (willApplyHistory) {
    lines.push(windowed != null
      ? { type: 'windowed_known', windowed, histDays }
      : { type: 'windowed_pending', histDays });
  } else if (requests != null) {
    lines.push({ type: 'requests', requests });
  } else {
    // Neither a history window nor a request count applies — no extra line.
  }
  return lines;
}

export function classifyExpectedCell({ enabled, refreshingHealth, health, cursorField }) {
  if (!enabled) {return { kind: 'disabled' };}
  if (refreshingHealth) {return { kind: 'checking' };}
  if (!health) {return { kind: NOT_CHECKED };}
  if (health.healthy === false) {return { kind: 'unhealthy', error: health.error };}
  const records = health.total_records;
  const requests = health.total_requests;
  // Healthy but the connector didn't expose totals — "ready" rather than
  // two useless dashes; the live count will tick up as the sync runs.
  if (records == null && requests == null) {return { kind: 'ready_no_totals' };}
  const windowed = health.windowed_records;
  const histDays = health.history_days;
  const willApplyHistory = !!(cursorField && histDays);
  const lines = expectedCellLines({ records, requests, windowed, histDays, willApplyHistory });
  return { kind: 'ready_with_totals', lines };
}

// One detail line under the "Expected" column's ready-with-totals state.
function ExpectedCellLine({ line }) {
  if (line.type === 'total') {
    return (
      <Tooltip title={`Source has ${line.records.toLocaleString()} total rows for this stream (no history window applied).`}>
        <Text style={{ fontSize: 10 }}>
          Total: <strong>{line.records.toLocaleString()}</strong>
        </Text>
      </Tooltip>
    );
  }
  if (line.type === 'windowed_known') {
    return (
      <Tooltip title={`Your first sync will pull ~${line.windowed.toLocaleString()} rows — those updated in the last ${line.histDays} days.`}>
        <Text type="secondary" style={{ fontSize: 10 }}>
          History ({line.histDays}d): <strong>{line.windowed.toLocaleString()}</strong>
        </Text>
      </Tooltip>
    );
  }
  if (line.type === 'windowed_pending') {
    return (
      <Text type="secondary" style={{ fontSize: 10, fontStyle: 'italic' }}>
        History ({line.histDays}d): re-check…
      </Text>
    );
  }
  return (
    <Text type="secondary" style={{ fontSize: 10 }}>
      ~{line.requests.toLocaleString()} {line.requests === 1 ? 'req' : 'reqs'}
    </Text>
  );
}

// "Last status" column classification: whether the row is currently
// in-flight (running/queued/just-clicked) and, either way, which run
// phase badge (if any) applies.
// 'queued' must read as in-flight: with the Dagster runtime the run sits
// queued for a few seconds before flipping to 'running', and without
// this the spinner blinks off between the click finishing and the
// executor picking the run up.
function isStreamInFlight(last, syncingStreamName, streamName) {
  return (!!last && (last.status === 'running' || last.status === 'queued'))
    || syncingStreamName === streamName;
}

// Prefer the stream's own origin-run phase (success + transforming/
// extracted) while settled; EXTRACTING/QUEUED while in-flight; null once
// settled without that success case.
function resolveStreamPhase(last, runPhase, isInFlight) {
  if (last && last.status === 'success' && (
    runPhase === PHASE.TRANSFORMING || runPhase === PHASE.EXTRACTED
  )) {
    return runPhase;
  }
  if (isInFlight) {
    return runPhase === PHASE.QUEUED ? PHASE.QUEUED : PHASE.EXTRACTING;
  }
  return null;
}

function inFlightCellDetail(last, streamPhase) {
  return {
    kind: 'in_flight',
    streamPhase,
    loaded: (last && last.rows_loaded) || 0,
    requests: (last && last.requests_made) || 0,
    cancelling: !!(last && last.cancel_requested),
  };
}

export function classifyLastStatusCell({ last, syncingStreamName, streamName, runDetailPhase }) {
  const isInFlight = isStreamInFlight(last, syncingStreamName, streamName);
  // Prefer the stream's own origin-run phase so per-stream syncs running
  // in parallel don't clobber each other's row.
  const runPhase = last?.runPhase || runDetailPhase;
  const streamPhase = resolveStreamPhase(last, runPhase, isInFlight);
  if (isInFlight) {return inFlightCellDetail(last, streamPhase);}
  if (!last) {return { kind: 'empty' };}
  return { kind: 'terminal', streamPhase, status: last.status, stale: last.stale, error: last.error };
}

// "Actions" column classification: show a Cancel button only while the
// user's own click isn't still pending and a run to cancel is known;
// otherwise the per-row Sync button (loading while queued/just-clicked).
export function classifyActionsCell({ last, syncingStreamName, streamName, latestRunDetailId }) {
  const justClicked = syncingStreamName === streamName;
  const isRunning = !!(last && last.status === 'running');
  const isQueued = !!(last && last.status === 'queued');
  // While the user's click is still in flight (or the optimistic POST
  // hasn't yet surfaced as an active run), prefer the per-row sync
  // spinner over the cancel button so the click feels acknowledged
  // immediately.
  const cancelRunId = last?.runId || latestRunDetailId;
  const showCancel = isRunning && !justClicked && !!cancelRunId;
  return {
    mode: showCancel ? 'cancel' : 'sync',
    cancelRunId,
    cancelling: !!(last && last.cancel_requested),
    justClicked,
    isQueued,
  };
}

function StreamsTable({
  connection, spec, latestRunDetail, extraRunDetails, syncingStream, refreshingHealth,
  onOpen, onChange, onSyncOne,
}) {
  // Per-stream "last run that touched this stream" — accumulated across
  // run-detail updates rather than replaced. A per-stream sync creates a
  // run scoped to just that one stream, so the latest run's `streams`
  // array drops every other stream's history; without this accumulator
  // a second per-stream trigger blanks the first stream's row.
  // Entries are stamped with `runId`/`runPhase` so the cancel button
  // targets the correct Dagster run when streams are in flight under
  // different runs simultaneously.
  const [latestByStream, setLatestByStream] = useState({});
  const ingestDetail = useCallback((d) => {
    if (!d || !Array.isArray(d.streams)) {return;}
    setLatestByStream((prev) => {
      let changed = false;
      const next = prev;
      const out = { ...prev };
      for (const s of d.streams) {
        const prevEntry = next[s.stream];
        const newTs = new Date(s.started_at || 0).getTime();
        const prevTs = prevEntry ? new Date(prevEntry.started_at || 0).getTime() : 0;
        if (!prevEntry || newTs >= prevTs) {
          out[s.stream] = { ...s, runId: d.id, runPhase: d.phase };
          changed = true;
        }
      }
      return changed ? out : prev;
    });
  }, []);
  useEffect(() => { ingestDetail(latestRunDetail); }, [latestRunDetail, ingestDetail]);
  useEffect(() => {
    for (const d of extraRunDetails || []) {ingestDetail(d);}
  }, [extraRunDetails, ingestDetail]);

  // Stable references so the `data` memo below actually short-circuits.
  // `spec.streams || []` would otherwise allocate a new [] every render
  // when streams is null, busting the memo and causing the table to
  // re-render on every parent tick (visible flicker).
  const allStreams = useMemo(() => spec?.streams || [], [spec]);
  const enabled = useMemo(() => new Set(
    connection?.selected_streams || allStreams.map((s) => s.name),
  ), [connection?.selected_streams, allStreams]);
  const streamSchedules = useMemo(
    () => connection?.stream_schedules || {},
    [connection?.stream_schedules],
  );
  const streamHistory = useMemo(
    () => connection?.stream_history_days || {},
    [connection?.stream_history_days],
  );
  const streamLoadModes = useMemo(
    () => connection?.stream_load_modes || {},
    [connection?.stream_load_modes],
  );

  const toggleEnabled = async (streamNm, on) => {
    const current = new Set(connection.selected_streams || allStreams.map((s) => s.name));
    const streamMap = Object.fromEntries(allStreams.map((s) => [s.name, s]));

    if (on) {
      // Block enable if any hard dependency is currently disabled.
      const requires = streamMap[streamNm]?.requires || [];
      const missing = requires.filter((dep) => !current.has(dep));
      if (missing.length > 0) {
        Modal.error({
          title: `Can't enable "${streamNm}"`,
          content: (
            <div>
              <p>This stream depends on the following stream(s) which are currently disabled:</p>
              <ul>{missing.map((d) => <li key={d}><code>{d}</code></li>)}</ul>
              <p>Enable {missing.length === 1 ? 'it' : 'them'} first, then try again.</p>
            </div>
          ),
        });
        return;
      }
    } else {
      // Block disable if any other ENABLED stream depends on this one.
      const dependents = allStreams.filter(
        (s) => current.has(s.name) && (s.requires || []).includes(streamNm),
      );
      if (dependents.length > 0) {
        Modal.error({
          title: `Can't disable "${streamNm}"`,
          content: (
            <div>
              <p>The following enabled stream(s) depend on it and would break:</p>
              <ul>{dependents.map((d) => <li key={d.name}><code>{d.name}</code></li>)}</ul>
              <p>Disable {dependents.length === 1 ? 'it' : 'them'} first, then try again.</p>
            </div>
          ),
        });
        return;
      }
    }

    const next = new Set(current);
    if (on) {next.add(streamNm);} else {next.delete(streamNm);}
    await updateConnection(connection.id, { selected_streams: Array.from(next) });
    await onChange();
  };

  const setStreamSchedule = async (streamNm, value) => {
    if (value === CUSTOM) {
      const current = streamSchedules[streamNm] || '';
      let entered = current;
      Modal.confirm({
        title: `Custom schedule for "${streamNm}"`,
        icon: <CalendarOutlined />,
        content: (
          <div style={{ marginTop: 8 }}>
            <Input
              defaultValue={current}
              placeholder="e.g. */15 * * * *"
              onChange={(e) => { entered = e.target.value; }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Standard 5-field UTC cron. Falls back to the connection's default if blank.
            </Text>
          </div>
        ),
        onOk: async () => {
          const enteredCron = (entered || '').trim();
          if (!enteredCron) { message.warning('No cron entered'); return; }
          const nextSchedules = { ...streamSchedules, [streamNm]: enteredCron };
          await updateConnection(connection.id, { stream_schedules: nextSchedules });
          await onChange();
        },
      });
      return;
    }
    let cron = null;
    if (value !== INHERIT && value !== '__manual__') {cron = value;}
    const next = { ...streamSchedules };
    if (cron) {next[streamNm] = cron;} else {delete next[streamNm];}
    await updateConnection(connection.id, { stream_schedules: next });
    await onChange();
  };

  const setStreamHistory = async (streamNm, value) => {
    let days = null;
    if (value === CUSTOM) {
      const current = streamHistory[streamNm] || '';
      let entered = String(current);
      Modal.confirm({
        title: `Initial backfill window for "${streamNm}"`,
        icon: <CalendarOutlined />,
        content: (
          <div style={{ marginTop: 8 }}>
            <Input
              defaultValue={current}
              type="number"
              min={1}
              placeholder="e.g. 90"
              suffix="days"
              onChange={(e) => { entered = e.target.value; }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Caps how far back the very first sync extracts. Once a watermark exists,
              the connector resumes from there.
            </Text>
          </div>
        ),
        onOk: async () => {
          const n = parseInt(entered, 10);
          if (!n || n <= 0) { message.warning('Enter a positive number of days'); return; }
          const nextHistory = { ...streamHistory, [streamNm]: n };
          await updateConnection(connection.id, { stream_history_days: nextHistory });
          await onChange();
        },
      });
      return;
    }
    if (value !== '__all__') {
      days = parseInt(value, 10);
      if (!days || days <= 0) {days = null;}
    }
    const next = { ...streamHistory };
    if (days) {next[streamNm] = days;} else {delete next[streamNm];}
    await updateConnection(connection.id, { stream_history_days: next });
    await onChange();
  };

  const setStreamLoadMode = async (streamNm, value) => {
    const next = { ...streamLoadModes };
    if (value === INHERIT) {
      delete next[streamNm];
    } else if (value === 'full' || value === INCREMENTAL) {
      next[streamNm] = value;
    } else {
      delete next[streamNm];
    }
    await updateConnection(connection.id, { stream_load_modes: next });
    await onChange();
  };

  // Memoise the data array so antd's Table only re-diffs rows whose
  // underlying values actually changed. Without this, every parent
  // render builds a fresh `data` reference and antd virtualises new
  // row objects, triggering the whole table to repaint — the flicker
  // the user reported in the backlog.
  const data = useMemo(() => allStreams.map((s) => ({
    key: s.name,
    stream: s,
    enabled: enabled.has(s.name),
    schedule: streamSchedules[s.name] || null,
    history: streamHistory[s.name] || null,
    loadMode: streamLoadModes[s.name] || null,
    last: latestByStream[s.name],
    health: (connection.stream_test_result || {})[s.name] || null,
  })), [
    allStreams,
    enabled,
    streamSchedules,
    streamHistory,
    streamLoadModes,
    latestByStream,
    connection.stream_test_result,
  ]);

  const columns = [
    {
      title: <span style={{ fontSize: 12 }}>Table</span>,
      dataIndex: 'stream',
      render: (s) => {
        const requires = s.requires || [];
        return (
          <Space orientation="vertical" size={0}>
            <Text strong>{s.name}</Text>
            {requires.length > 0 && (
              <Tooltip title={`This stream cannot sync unless ${requires.length === 1 ? 'this stream is' : 'these streams are'} also enabled.`}>
                <Text type="warning" style={{ fontSize: 10 }}>
                  <ApiOutlined style={{ marginRight: 4 }} />
                  requires: {requires.map((t, i) => (
                    <React.Fragment key={t}>
                      {i > 0 && ', '}
                      <code style={{ fontSize: 10 }}>{t}</code>
                    </React.Fragment>
                  ))}
                </Text>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: <span style={{ fontSize: 12 }}>Enabled</span>,
      dataIndex: 'enabled',
      width: 56,
      render: (on, row) => (
        <Switch
          size="small"
          checked={on}
          onChange={(v) => toggleEnabled(row.stream.name, v)}
          onClick={(_, e) => e.stopPropagation()}
        />
      ),
    },
    {
      title: <span style={{ fontSize: 12 }}>Schedule</span>,
      dataIndex: 'schedule',
      width: 140,
      render: (cron, row) => {
        const matchesPreset = cron && SCHEDULE_PRESETS.some((p) => p.value === cron);
        let value;
        if (!cron) {value = INHERIT;} else if (matchesPreset) {value = cron;} else {value = CUSTOM;}
        return (
          <Select
            size="small"
            style={{ width: '100%', fontSize: 10 }}
            value={value}
            onChange={(v) => setStreamSchedule(row.stream.name, v)}
            onClick={(e) => e.stopPropagation()}
            options={SCHEDULE_PRESETS.map((p) => ({
              value: p.value,
              label: p.value === CUSTOM && cron && !matchesPreset ? `Custom: ${cron}` : p.label,
            }))}
          />
        );
      },
    },
    {
      title: (
        <Tooltip title="Per-stream override of how data is loaded. Delta = use cursor watermark and only fetch new/changed rows. Full = re-extract everything and replace the table on each run. Inherit falls back to the connection-level mode.">
          <span style={{ fontSize: 12 }}>Load mode <Text type="secondary" style={{ fontSize: 10 }}>(?)</Text></span>
        </Tooltip>
      ),
      dataIndex: 'loadMode',
      width: 110,
      render: (mode, row) => {
        const value = mode || INHERIT;
        return (
          <Select
            size="small"
            style={{ width: '100%', fontSize: 10 }}
            value={value}
            onChange={(v) => setStreamLoadMode(row.stream.name, v)}
            onClick={(e) => e.stopPropagation()}
            options={LOAD_MODE_OPTIONS.map((o) => ({
              value: o.value,
              label: loadModeOptionLabel(o, connection),
            }))}
          />
        );
      },
    },
    {
      title: (
        <Tooltip title="How far back to extract on the first sync. Only meaningful for streams with a cursor field (transactional data like commits, PRs, incidents).">
          <span style={{ fontSize: 12 }}>History <Text type="secondary" style={{ fontSize: 10 }}>(?)</Text></span>
        </Tooltip>
      ),
      dataIndex: 'history',
      width: 100,
      render: (days, row) => {
        if (!row.stream.cursor_field) {
          return <Text type="secondary" style={{ fontSize: 10 }}>n/a</Text>;
        }
        const matchesPreset = days && HISTORY_PRESETS.some((p) => String(p.days) === String(days));
        let value;
        if (!days) {value = '__all__';} else if (matchesPreset) {value = String(days);} else {value = CUSTOM;}
        return (
          <Select
            size="small"
            style={{ width: '100%', fontSize: 10 }}
            value={value}
            onChange={(v) => setStreamHistory(row.stream.name, v)}
            onClick={(e) => e.stopPropagation()}
            options={HISTORY_PRESETS.map((p) => ({
              value: p.value,
              label: p.value === CUSTOM && days && !matchesPreset
                ? `Custom: ${days} days`
                : p.label,
            }))}
          />
        );
      },
    },
    {
      title: <span style={{ fontSize: 12 }}>Expected</span>,
      dataIndex: 'health',
      width: 130,
      render: (h, row) => {
        const info = classifyExpectedCell({
          enabled: row.enabled, refreshingHealth, health: h, cursorField: row.stream.cursor_field,
        });
        if (info.kind === 'disabled') {return <Text type="secondary" style={{ fontSize: 10 }}>—</Text>;}
        if (info.kind === 'checking') {
          return (
            <Text type="secondary" style={{ fontSize: 10 }}>
              <SyncOutlined spin style={{ color: '#1677ff', marginRight: 4 }} />
              checking…
            </Text>
          );
        }
        if (info.kind === NOT_CHECKED) {
          return (
            <Tooltip title="Click 'Test connection' above to probe this stream">
              <Text type="secondary" style={{ fontSize: 10 }}>not checked</Text>
            </Tooltip>
          );
        }
        if (info.kind === 'unhealthy') {
          return (
            <Tooltip title={info.error || 'unhealthy'}>
              <Tag color="error" style={{ margin: 0, fontSize: 10 }}>
                <CloseCircleFilled /> failed
              </Tag>
            </Tooltip>
          );
        }
        if (info.kind === 'ready_no_totals') {
          return (
            <Tooltip title="Credentials valid. This connector doesn't expose a row count up-front — the live counter will tally records as the sync runs.">
              <Tag color="success" style={{ margin: 0, fontSize: 10 }}>
                <CheckCircleFilled /> ready
              </Tag>
            </Tooltip>
          );
        }
        return (
          <Space size={6} align="start">
            <Tooltip title="Credentials valid. Numbers below are an estimate from a single probe call.">
              <CheckCircleFilled style={{ color: '#52c41a', fontSize: 13, marginTop: 2 }} />
            </Tooltip>
            <Space orientation="vertical" size={0}>
              {info.lines.map((line, i) => <ExpectedCellLine key={i} line={line} />)}
            </Space>
          </Space>
        );
      },
    },
    {
      title: <span style={{ fontSize: 12 }}>Last status</span>,
      dataIndex: 'last',
      width: 150,
      render: (last, row) => {
        const info = classifyLastStatusCell({
          last, syncingStreamName: syncingStream, streamName: row.stream.name,
          runDetailPhase: latestRunDetail?.phase,
        });
        if (info.kind === 'empty') {return <Text type="secondary">—</Text>;}
        if (info.kind === 'in_flight') {
          return (
            <Space orientation="vertical" size={2}>
              {info.streamPhase && !info.cancelling && <PhaseTag phase={info.streamPhase} compact />}
              <Text style={{ fontSize: 10 }}>
                {info.cancelling ? (
                  <><StopOutlined style={{ color: '#faad14', marginRight: 4 }} /> Cancelling…</>
                ) : (
                  <><SyncOutlined spin style={{ color: '#1677ff', marginRight: 4 }} />
                    <strong>{info.loaded.toLocaleString()}</strong> rows
                  </>
                )}
              </Text>
              {!info.cancelling && (
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {info.requests.toLocaleString()} {info.requests === 1 ? 'request' : 'requests'}
                </Text>
              )}
            </Space>
          );
        }
        return (
          <Space orientation="vertical" size={2}>
            <StatusTag status={info.status} stale={info.stale} tooltip={info.error} />
            {info.streamPhase && <PhaseTag phase={info.streamPhase} compact />}
          </Space>
        );
      },
    },
    {
      title: <span style={{ fontSize: 12 }}>Rows</span>,
      dataIndex: 'last',
      width: 76,
      render: (last) => (
        <span style={{ fontSize: 11 }}>
          {last ? (last.rows_loaded || 0).toLocaleString() : '—'}
        </span>
      ),
    },
    {
      title: <span style={{ fontSize: 12 }}>When</span>,
      dataIndex: 'last',
      width: 68,
      render: (last) => (
        <span style={{ fontSize: 11 }}>
          {last ? timeAgo(last.finished_at || last.started_at) : '—'}
        </span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 36,
      align: 'center',
      render: (_, row) => {
        const info = classifyActionsCell({
          last: row.last, syncingStreamName: syncingStream, streamName: row.stream.name,
          latestRunDetailId: latestRunDetail?.id,
        });
        if (info.mode === 'cancel') {
          return (
            <Tooltip title={info.cancelling ? 'Cancelling…' : `Cancel sync of ${row.stream.name}`}>
              <Button
                size="small"
                danger
                type="text"
                icon={<StopOutlined />}
                loading={info.cancelling}
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await cancelSyncStream(info.cancelRunId, row.stream.name);
                    message.info(`Cancelling "${row.stream.name}"…`);
                    await onChange();
                  } catch (err) {
                    message.error(`Cancel failed: ${err.message}`);
                  }
                }}
              />
            </Tooltip>
          );
        }
        return (
          <Tooltip title={row.enabled ? `Sync ${row.stream.name} now` : 'Enable this stream first'}>
            <Button
              size="small"
              type="text"
              icon={<ThunderboltOutlined />}
              loading={info.justClicked || info.isQueued}
              disabled={!row.enabled}
              onClick={(e) => {
                e.stopPropagation();
                onSyncOne(row.stream.name);
              }}
            />
          </Tooltip>
        );
      },
    },
  ];

  // Defer the early-return until after every hook has been called, so
  // we never violate the rules-of-hooks ordering between renders.
  if (!connection || !spec) {return null;}

  return (
    <Table
      size="small"
      columns={columns}
      dataSource={data}
      pagination={false}
      scroll={{ x: SCROLL_MAX_CONTENT }}
      // Stable per-stream rowKey lets antd reuse row instances across
      // re-renders. Without this, antd defaults to the row index, and
      // every poll-driven dataSource update re-keys every row → React
      // unmounts + remounts the cells, which is the visible flicker.
      rowKey={(row) => `stream-${row.stream.name}`}
      onRow={(row) => ({
        style: { cursor: 'pointer' },
        onClick: () => onOpen(row.stream.name),
      })}
    />
  );
}

// ─────────────────────────────────────────────────────────────
// Stream detail drawer — tabbed table preview.
//
// Surfaces the four things a user typically wants before/after wiring up
// a source: a one-glance overview, the table catalog (columns + types
// inferred from cached Test Connection samples), sample rows, and the
// per-stream sync history. The catalog + sample-data tabs are populated
// from `connection.stream_test_result[streamName]` — populated whenever
// the user runs Test Connection — so previews are instant and don't hit
// the source on every drawer open.
// ─────────────────────────────────────────────────────────────

// ISO-8601-ish timestamp detector — kept loose so partial dates count.
const TIMESTAMP_LIKE = /^\d{4}-\d{2}-\d{2}([T\s]\d{2}:\d{2}|$)/;

// Classify a single non-null sample value into the coarse "Type" column
// label used by the catalog tab.
export function classifySampleValue(v) {
  if (Array.isArray(v)) {return 'array';}
  if (typeof v === 'object') {return 'object';}
  if (typeof v === 'boolean') {return 'boolean';}
  if (typeof v === 'number') {return Number.isInteger(v) ? 'integer' : 'number';}
  if (typeof v === 'string') {return TIMESTAMP_LIKE.test(v) ? 'timestamp' : 'string';}
  return typeof v;
}

// Coarse type inference from a column's first non-null sample value.
// Used to fill the "Type" column of the catalog tab. Intentionally
// simple — we render this as an advisory hint, not as a strict schema.
export function inferColumnType(values) {
  for (const v of values) {
    if (v === null || v === undefined) {continue;}
    return classifySampleValue(v);
  }
  return 'unknown';
}

// Render a sample-data cell. JSON objects/arrays get collapsed to a
// one-line preview so the table stays readable; long strings get
// truncated with the full value available on hover.
function SampleCell({ value }) {
  if (value === null || value === undefined) {
    return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
  }
  if (typeof value === 'boolean') {
    return <Tag color={value ? 'green' : 'default'} style={{ margin: 0 }}>{String(value)}</Tag>;
  }
  if (Array.isArray(value) || typeof value === 'object') {
    const json = JSON.stringify(value);
    const preview = json.length > SAMPLE_PREVIEW_MAX_CHARS ? json.slice(0, SAMPLE_PREVIEW_MAX_CHARS) + '…' : json;
    return (
      <Tooltip title={<pre style={{ margin: 0, maxHeight: 320, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{JSON.stringify(value, null, 2)}</pre>}>
        <code style={{ fontSize: 11 }}>{preview}</code>
      </Tooltip>
    );
  }
  const str = String(value);
  if (str.length > 64) {
    return (
      <Tooltip title={str} placement="topLeft">
        <span style={{ fontSize: 12 }}>{str.slice(0, 64)}…</span>
      </Tooltip>
    );
  }
  return <span style={{ fontSize: 12 }}>{str}</span>;
}

// The connector's description, with a link to its official docs.
function SourceDescriptionAlert({ spec, description, docsUrl }) {
  return (
    <Alert
      type="info"
      showIcon
      icon={<InfoCircleOutlined />}
      title={spec.display_name || spec.name}
      description={
        <Space orientation="vertical" size={6} style={{ width: '100%' }}>
          <Text>{description}</Text>
          {docsUrl && (
            <a href={docsUrl} target="_blank" rel="noreferrer">
              <Space size={4}>
                <BookOutlined />
                <span>Reference: official documentation</span>
                <ExportOutlined style={{ fontSize: 11 }} />
              </Space>
            </a>
          )}
        </Space>
      }
    />
  );
}

// Table metadata: PK, cursor, references, dependencies, effective schedule.
function TableMetadata({ stream, references, requires, effectiveSchedule, scheduleOverride }) {
  return (
    <Descriptions
      column={1}
      size="small"
      bordered
      title={<Space><DatabaseOutlined /> <span>Table metadata</span></Space>}
    >
      <Descriptions.Item label="Table">
        <code>{stream.name}</code>
      </Descriptions.Item>
      <Descriptions.Item label="Primary key">
        {(stream.primary_key || []).length > 0
          ? <code>{stream.primary_key.join(', ')}</code>
          : <Text type="secondary">none</Text>}
      </Descriptions.Item>
      <Descriptions.Item label="Cursor field">
        {stream.cursor_field
          ? <><code>{stream.cursor_field}</code> <Text type="secondary">(incremental)</Text></>
          : <Text type="secondary">none (full refresh)</Text>}
      </Descriptions.Item>
      {references.length > 0 && (
        <Descriptions.Item label="References">
          <Space orientation="vertical" size={2}>
            {references.map((r, i) => (
              <Text key={i} style={{ fontSize: 12 }}>
                <code>{r.column}</code> → <code>{r.target_stream}.{r.target_column}</code>
              </Text>
            ))}
          </Space>
        </Descriptions.Item>
      )}
      {requires.length > 0 && (
        <Descriptions.Item label="Requires">
          <Space size={4} wrap>
            {requires.map((r) => <Tag key={r}><code>{r}</code></Tag>)}
          </Space>
        </Descriptions.Item>
      )}
      <Descriptions.Item label="Effective schedule">
        {effectiveSchedule
          ? <>
              <Tag color="blue" icon={<CalendarOutlined />}>{effectiveSchedule}</Tag>
              {!scheduleOverride && <Text type="secondary">(inherited from connection)</Text>}
            </>
          : <Text type="secondary">manual only</Text>}
      </Descriptions.Item>
    </Descriptions>
  );
}

// Last Test Connection probe: source total and first-sync window rows.
function ProbeStats({ testResult }) {
  return (
    <Row gutter={12}>
      {testResult.total_records != null && (
        <Col span={12}>
          <Card size="small">
            <Statistic
              title="Source total rows"
              value={testResult.total_records}
              styles={{ content: { fontSize: 18 } }}
            />
          </Card>
        </Col>
      )}
      {testResult.windowed_records != null && (
        <Col span={12}>
          <Card size="small">
            <Statistic
              title={`First sync (${testResult.history_days || '?'}d window)`}
              value={testResult.windowed_records}
              styles={{ content: { fontSize: 18 } }}
            />
          </Card>
        </Col>
      )}
    </Row>
  );
}

function StreamOverviewTab({ stream, spec, testResult, effectiveSchedule, scheduleOverride }) {
  const references = stream.references || [];
  const requires = stream.requires || [];
  const docsUrl = spec.docs_url || '';
  const description = spec.description || '';

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      {description && <SourceDescriptionAlert spec={spec} description={description} docsUrl={docsUrl} />}

      <TableMetadata
        stream={stream} references={references} requires={requires}
        effectiveSchedule={effectiveSchedule} scheduleOverride={scheduleOverride}
      />

      {testResult && (testResult.total_records != null || testResult.windowed_records != null) && (
        <ProbeStats testResult={testResult} />
      )}
    </Space>
  );
}

function StreamCatalogTab({ stream, testResult }) {
  const cols = testResult?.sample_columns || [];
  const rows = testResult?.sample_rows || [];

  if (!testResult || cols.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Space orientation="vertical" size={4} align="center">
            <Text type="secondary">
              No catalog cached yet for this table.
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Run <strong>Test connection</strong> on this source — the
              column list and types are inferred from a small live probe.
            </Text>
          </Space>
        }
        style={{ padding: '40px 0' }}
      />
    );
  }

  const pkSet = new Set(stream.primary_key || []);
  const cursor = stream.cursor_field || null;

  const data = cols.map((col) => {
    const samples = rows.map((r) => r?.[col]);
    const nonNull = samples.filter((v) => v !== null && v !== undefined);
    return {
      key: col,
      column: col,
      type: inferColumnType(samples),
      isPk: pkSet.has(col),
      isCursor: cursor === col,
      nullable: nonNull.length < samples.length,
      example: nonNull.length > 0 ? nonNull[0] : null,
    };
  });

  const columns = [
    {
      title: 'Column',
      dataIndex: 'column',
      render: (c, row) => (
        <Space size={4}>
          <code style={{ fontSize: 12 }}>{c}</code>
          {row.isPk && <Tag color="gold" style={{ margin: 0, fontSize: 10 }}>PK</Tag>}
          {row.isCursor && <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>cursor</Tag>}
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      width: 110,
      render: (t) => <Tag style={{ margin: 0, fontSize: 11 }}>{t}</Tag>,
    },
    {
      title: 'Nullable',
      dataIndex: 'nullable',
      width: 80,
      align: 'center',
      render: (n) => n
        ? <Text type="secondary" style={{ fontSize: 11 }}>yes</Text>
        : <Text style={{ fontSize: 11 }}>no</Text>,
    },
    {
      title: 'Example',
      dataIndex: 'example',
      ellipsis: true,
      render: (v) => <SampleCell value={v} />,
    },
  ];

  return (
    <>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
        Catalog inferred from <strong>{rows.length}</strong> sample {rows.length === 1 ? 'row' : 'rows'} captured by Test Connection.
      </Text>
      <Table
        size="small"
        columns={columns}
        dataSource={data}
        pagination={false}
        rowKey="column"
        scroll={{ x: SCROLL_MAX_CONTENT }}
      />
    </>
  );
}

function StreamSampleDataTab({ testResult }) {
  const cols = testResult?.sample_columns || [];
  const rows = testResult?.sample_rows || [];

  if (!testResult || rows.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Space orientation="vertical" size={4} align="center">
            <Text type="secondary">No sample rows cached.</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Run <strong>Test connection</strong> to pull a handful of
              real rows from the source — they'll show up here.
            </Text>
          </Space>
        }
        style={{ padding: '40px 0' }}
      />
    );
  }

  const columns = cols.map((col) => ({
    title: <code style={{ fontSize: 11 }}>{col}</code>,
    dataIndex: col,
    key: col,
    width: 200,
    ellipsis: true,
    render: (v) => <SampleCell value={v} />,
  }));

  const data = rows.map((r, i) => ({ key: i, ...r }));

  return (
    <>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
        Showing {rows.length} sample {rows.length === 1 ? 'row' : 'rows'} captured by Test Connection.
      </Text>
      <Table
        size="small"
        columns={columns}
        dataSource={data}
        pagination={rows.length > 10 ? { pageSize: 10, size: 'small' } : false}
        scroll={{ x: SCROLL_MAX_CONTENT, y: 480 }}
      />
    </>
  );
}

// Timeline dot color for one sync-log entry's status.
export function statusTimelineColor(status) {
  if (status === 'success' || status === 'succeeded') {return 'green';}
  if (status === 'failed') {return 'red';}
  if (status === 'running') {return 'blue';}
  return 'gray';
}

function StreamSyncLogsTab({ history, loading }) {
  if (loading) {return <Spin />;}
  if (!history || history.length === 0) {
    return <Empty description="No runs yet for this table." />;
  }
  // Render the error alert description as JSX so the embedded run id
  // becomes a hyperlink into the Dagster UI. The error_message text is
  // composed as "Dagster run <id> ended with status <status>." so we
  // split around the id when run_id is present.
  const renderErrorDescription = (h) => {
    if (!h.run_id || !h.error_message) {return h.error_message;}
    // With the Dagit proxy off there is nowhere for the link to go, so the
    // message stays plain text — the run id is still in it, which is what an
    // operator needs to look the run up via /syncs/dagit/runs/{id}/logs.
    if (!isDagitEnabled()) {return h.error_message;}
    const shortId = String(h.run_id).slice(0, 8);
    const idx = h.error_message.indexOf(shortId);
    if (idx === -1) {
      return (
        <Space orientation="vertical" size={2} style={{ width: '100%' }}>
          <span>{h.error_message}</span>
          <a href={dagsterRunUrl(h.run_id)} target="_blank" rel="noreferrer"
             onClick={(e) => { e.preventDefault(); openDagsterRun(h.run_id); }}>
            View run in Dagster <ExportOutlined style={{ fontSize: 11 }} />
          </a>
        </Space>
      );
    }
    return (
      <span>
        {h.error_message.slice(0, idx)}
        <a href={dagsterRunUrl(h.run_id)} target="_blank" rel="noreferrer"
           onClick={(e) => { e.preventDefault(); openDagsterRun(h.run_id); }}>
          {shortId} <ExportOutlined style={{ fontSize: 11 }} />
        </a>
        {h.error_message.slice(idx + shortId.length)}
      </span>
    );
  };
  return (
    <Timeline
      items={history.map((h) => ({
        color: statusTimelineColor(h.status),
        content: (
          <Space orientation="vertical" size={2} style={{ width: '100%' }}>
            <Space>
              <StatusTag status={h.status} />
              <Text type="secondary">{timeAgo(h.started_at)}</Text>
              <Text type="secondary">· {(h.rows_loaded || 0).toLocaleString()} rows</Text>
              {h.http_status && <Tag>HTTP {h.http_status}</Tag>}
            </Space>
            {h.error_message && (
              <Alert
                type="error"
                showIcon
                title={h.error_type || 'Error'}
                description={renderErrorDescription(h)}
                style={{ marginTop: 4 }}
              />
            )}
          </Space>
        ),
      }))}
    />
  );
}

// Dagster-native status -> the legacy value StatusTag and the sync-log
// Timeline color picker key off. Any status not in the table (including
// empty) passes through as-is, falling back to 'unknown' only when
// there's no status at all — same as the if-chain this replaces.
const DAGSTER_STATUS_MAP = {
  success: 'success',
  failure: 'failed', failed: 'failed',
  started: 'running', running: 'running',
  canceled: 'cancelled', cancelled: 'cancelled', canceling: 'cancelled',
  queued: 'queued', starting: 'queued', not_started: 'queued', managed: 'queued',
};

export function normalizeDagsterStatus(status) {
  const k = String(status || '').toLowerCase();
  return DAGSTER_STATUS_MAP[k] || k || 'unknown';
}

// Strip a Dagster asset key's connector-name prefix (e.g.
// "github__pull_requests" -> "pull_requests") so it can be compared
// against a bare stream name. `key` may be the array form Dagit returns
// for compound keys.
export function stripAssetKeyPrefix(key, prefix) {
  const flat = Array.isArray(key) ? key.join('.') : String(key || '');
  return flat.startsWith(prefix) ? flat.slice(prefix.length) : flat;
}

// Runs whose asset_selection touched this stream — a whole-job run (empty
// asset_selection) always counts, since it touches every stream.
export function filterRunsForStream(runs, streamName, prefix) {
  return (runs || []).filter((r) => {
    const sel = r.asset_selection || [];
    if (sel.length === 0) {return true;}
    return sel.some((a) => stripAssetKeyPrefix(a, prefix) === streamName);
  });
}

// Fetch each run's Dagit detail (materializations etc.), tolerating
// individual fetch failures (null for any run whose detail didn't load).
export function fetchDagitRunDetails(relevant) {
  return Promise.all(relevant.map((r) => getDagitRun(r.id).catch(() => null)));
}

// Build StreamSyncLogsTab rows from a batch of Dagit run + materialization
// detail fetches, one per `relevant` run (same order, nulls for any fetch
// that failed). A materialization is the strongest signal that *this*
// stream succeeded, even if a later stream failed and tanked the run; a
// whole-job success run with no materialization for this stream didn't
// actually process it and is dropped to avoid a phantom "no rows" row.
function matchingStreamMaterialization(detail, prefix, streamName) {
  return (detail.materializations || []).find(
    (m) => stripAssetKeyPrefix(m.asset_key, prefix) === streamName,
  );
}

// Whole-job success run with no materialization for this stream didn't
// actually process it — the "phantom no-rows row" to drop.
function isPhantomWholeJobHistoryRow(run, mat, runStatus) {
  const wholeJob = (run.asset_selection || []).length === 0;
  return !mat && wholeJob && runStatus === 'success';
}

// A materialization is the strongest signal that *this* stream
// succeeded, even if a later stream failed and tanked the run.
function streamHistoryRowStatus(mat, runStatus) {
  if (mat) {return 'success';}
  return runStatus === 'failed' ? 'failed' : runStatus;
}

function streamHistoryErrorFields(status, run) {
  if (status !== 'failed') {return { error_type: null, error_message: null };}
  return {
    error_type: 'Run failed',
    error_message: `Dagster run ${String(run.id).slice(0, 8)} ended with status ${run.status}.`,
  };
}

export function buildStreamHistoryRows({ relevant, details, streamName, prefix }) {
  return details.map((d, i) => {
    const run = relevant[i];
    if (!d) {return null;}
    const mat = matchingStreamMaterialization(d, prefix, streamName);
    const runStatus = normalizeDagsterStatus(run.status);
    if (isPhantomWholeJobHistoryRow(run, mat, runStatus)) {return null;}
    const meta = (mat && mat.metadata) || {};
    const status = streamHistoryRowStatus(mat, runStatus);
    const { error_type, error_message } = streamHistoryErrorFields(status, run);
    return {
      run_id: run.id,
      stream: streamName,
      status,
      rows_loaded: mat ? Number(meta.rows_loaded || 0) : 0,
      started_at: run.start_time || run.creation_time,
      finished_at: mat ? mat.timestamp : run.end_time,
      error_type,
      error_message,
      http_status: null,
    };
  }).filter(Boolean);
}

// The stream drawer's tabs; Catalog and Sample data carry a count badge
// once the last probe returned columns / rows.
function streamTabItems({
  stream, spec, connection, testResult, effectiveSchedule, scheduleOverride, history, loading,
}) {
  const sampleCount = (testResult?.sample_rows || []).length;
  const columnCount = (testResult?.sample_columns || []).length;

  return [
    {
      key: 'overview',
      label: <Space size={4}><InfoCircleOutlined /> Overview</Space>,
      children: (
        <StreamOverviewTab
          stream={stream}
          spec={spec}
          connection={connection}
          testResult={testResult}
          effectiveSchedule={effectiveSchedule}
          scheduleOverride={scheduleOverride}
        />
      ),
    },
    {
      key: 'catalog',
      label: (
        <Space size={4}>
          <TableOutlined />
          <span>Catalog</span>
          {columnCount > 0 && <Badge count={columnCount} size="small" style={{ backgroundColor: '#1677ff' }} />}
        </Space>
      ),
      children: <StreamCatalogTab stream={stream} testResult={testResult} />,
    },
    {
      key: 'sample',
      label: (
        <Space size={4}>
          <FileTextOutlined />
          <span>Sample data</span>
          {sampleCount > 0 && <Badge count={sampleCount} size="small" style={{ backgroundColor: '#52c41a' }} />}
        </Space>
      ),
      children: <StreamSampleDataTab testResult={testResult} />,
    },
    {
      key: 'logs',
      label: <Space size={4}><HistoryOutlined /> Sync logs</Space>,
      children: <StreamSyncLogsTab history={history} loading={loading} />,
    },
    {
      key: 'connection',
      label: <Space size={4}><ApiOutlined /> Connection</Space>,
      children: (
        <StreamConnectionTab
          stream={stream}
          spec={spec}
          testResult={testResult}
          connection={connection}
        />
      ),
    },
  ];
}

function StreamDetailDrawer({ open, connection, spec, streamName, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const stream = useMemo(
    () => (spec?.streams || []).find((s) => s.name === streamName) || null,
    [spec, streamName],
  );

  const testResult = useMemo(() => {
    if (!connection || !streamName) {return null;}
    return (connection.stream_test_result || {})[streamName] || null;
  }, [connection, streamName]);

  // History is sourced from Dagster. The legacy `/syncs/streams/history`
  // endpoint reads `sync_stream_runs`, which the Dagster cutover stopped
  // populating, so it always returned []. We fetch the connection's runs,
  // keep those whose asset_selection touched this stream (whole-job runs
  // included), then pull each run's materializations for the per-stream
  // rowcount + timing. Mirrors DataCenter.js:SyncHistoryTab.
  useEffect(() => {
    if (!open || !connection || !streamName) {return undefined;}
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const runs = await listDagitRuns({
          connectionId: connection.id,
          limit: 50,
        }).catch(() => []);
        const prefix = `${(connection.connector_name || '').replace(/-/g, '_')}__`;
        const relevant = filterRunsForStream(runs, streamName, prefix).slice(0, STREAM_HISTORY_MAX_RUNS);
        const details = await fetchDagitRunDetails(relevant);
        const rows = buildStreamHistoryRows({ relevant, details, streamName, prefix });
        if (!cancelled) {setHistory(rows);}
      } catch (err) {
        if (!cancelled) {message.error(`History load failed: ${err.message}`);}
      } finally {
        if (!cancelled) {setLoading(false);}
      }
    })();
    return () => { cancelled = true; };
  }, [open, connection, streamName]);

  // Reset to the overview tab whenever the user opens a different stream
  // so muscle memory ("click table → see the description first") works.
  useEffect(() => {
    if (open) {setActiveTab('overview');}
  }, [open, streamName]);

  if (!stream || !connection) {
    return <Drawer open={open} onClose={onClose} size={760} />;
  }

  const scheduleOverride = (connection.stream_schedules || {})[streamName];
  const inherited = connection.schedule;
  const effectiveSchedule = scheduleOverride || inherited || null;

  const docsUrl = spec.docs_url || '';

  const tabItems = streamTabItems({
    stream, spec, connection, testResult, effectiveSchedule, scheduleOverride, history, loading,
  });

  return (
    <Drawer
      title={
        <Space>
          <Text strong>{stream.name}</Text>
          <Tag>{spec.display_name || spec.name}</Tag>
        </Space>
      }
      extra={docsUrl ? (
        <Tooltip title="Open the source's official API documentation in a new tab.">
          <Button
            size="small"
            icon={<BookOutlined />}
            href={docsUrl}
            target="_blank"
            rel="noreferrer"
          >
            Docs <ExportOutlined style={{ fontSize: 11 }} />
          </Button>
        </Tooltip>
      ) : null}
      size={780}
      open={open}
      onClose={onClose}
      destroyOnHidden
    >
      <Tabs
        size="small"
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />
    </Drawer>
  );
}
